import { ArrowDown, BookOpen, Brain, ChevronLeft, ChevronRight, CircleHelp, ClipboardCheck, Columns3, Eye, Focus, Grid2X2, Library, Minus, ScanSearch } from 'lucide-react';
import { ApiService } from '../../../services/api';
import React from 'react';

export const MiniLine: React.FC<{ width: number; color: string; className?: string }> = ({
  width,
  color,
  className = '',
}) => (
  <span
    className={`block h-[5px] rounded-full opacity-40 ${className}`}
    style={{ width: `${width}px`, backgroundColor: color }}
  />
);

export const LINES = [72, 58, 64, 52, 68];

export type ModeConfig = {
  id?: string;
  name: string;
  tone: string;
  accent: string;
  illustration: string;
  canonicalMode?: string;
  sectionId?: string;
  description?: string;
  status?: 'active' | 'planned' | 'deprecated' | 'hidden';
  uiCapabilities?: string[];
  legacyNames?: string[];
};

export type ModeSection = {
  id: string;
  title: string;
  description?: string;
  order?: number;
  /** Tenant'a özel bölüm görseli (Settings.speedReadingSectionImages). Yoksa çizim kullanılır. */
  imageUrl?: string;
};

export type ProgramCatalogExercise = {
  id: string;
  title: string;
  sectionId: string;
  canonicalMode: string;
  status: 'active' | 'planned' | 'deprecated' | 'hidden';
  description?: string;
  uiCapabilities?: string[];
  legacyNames?: string[];
};

export type ProgramCatalogResponse = {
  sections: ModeSection[];
  exercises: ProgramCatalogExercise[];
};

const MODE_VISUALS: Record<string, Pick<ModeConfig, 'tone' | 'accent' | 'illustration'>> = {
  grouping: { tone: '#E9F5F0', accent: '#89A9C0', illustration: 'group' },
  golgeleme: { tone: '#E4F2F9', accent: '#92B5CB', illustration: 'shadow' },
  bloklama: { tone: '#E8F0F5', accent: '#8DAFC4', illustration: 'block' },
  'blok-okuma': { tone: '#EEF2F4', accent: '#155263', illustration: 'blockReading' },
  'metin-okuma': { tone: '#F3EFE6', accent: '#9DB8CA', illustration: 'text' },
  'eksiltili-metin': { tone: '#EEF2F5', accent: '#8CA9C2', illustration: 'incompleteText' },
  takistoskop: { tone: '#EAF1F0', accent: '#8EB3BD', illustration: 'pilot' },
  'bir-bakista-gorelim': { tone: '#F3F6F7', accent: '#ff0000', illustration: 'wordGlance' },
  'gozlerimizi-sektirerek-gorelim': { tone: '#F3F6F7', accent: '#ff0000', illustration: 'jumpGlance' },
  eslestirme: { tone: '#EDF2F7', accent: '#8AAEC4', illustration: 'match' },
  'multi-fokus': { tone: '#F0EEF5', accent: '#97ADCC', illustration: 'multifocus' },
  'zit-anlam': { tone: '#F3F0EA', accent: '#A0B4C0', illustration: 'antonym' },
  'es-anlam': { tone: '#E6F5F0', accent: '#85B8C2', illustration: 'synonym' },
  'somut-anlam': { tone: '#F4F2E7', accent: '#A3B6C5', illustration: 'concrete' },
  'hedef-yakalama': { tone: '#F1EEF7', accent: '#97B0C6', illustration: 'capture' },
  'kaybolan-metin': { tone: '#EEF2F5', accent: '#8CA9C2', illustration: 'disappearingText' },
  'metin-arama': { tone: '#EEF2F5', accent: '#7E9FBE', illustration: 'textSearch' },
  'kelime-arama': { tone: '#EAF1F0', accent: '#8EB3BD', illustration: 'wordSearch' },
  'benzer-kelimeler': { tone: '#E6F5F0', accent: '#85B8C2', illustration: 'similarWords' },
  'dikkat-puzzle': { tone: '#F2F5F2', accent: '#7EA58E', illustration: 'attentionPuzzle' },
  'rengi-bul': { tone: '#F5F2F1', accent: '#C45F5F', illustration: 'colorFind' },
  'harf-sayma': { tone: '#F1F5F7', accent: '#6E9FB7', illustration: 'letterCount' },
  'sayi-sayma': { tone: '#F5F4EF', accent: '#A48E5D', illustration: 'numberCount' },
  'iki-resim-arasindaki-fark': { tone: '#F3F1F7', accent: '#9277B5', illustration: 'spotDifference' },
  'sihirli-hiz': { tone: '#EEF3E9', accent: '#A7C0D2', illustration: 'magic' },
  'odaklanarak-okuma': { tone: '#E6F0F5', accent: '#8BB3C8', illustration: 'focused' },
  'serbest-okuma': { tone: '#EAF4EE', accent: '#6E9E8E', illustration: 'text' },
  'okuma-anlama-seviye-testi': { tone: '#F2F1EA', accent: '#94A59D', illustration: 'text' },
  'anlama-calismalari': { tone: '#EEF3F7', accent: '#88A8B7', illustration: 'text' },
  'gunluk-odevler': { tone: '#F0F4EF', accent: '#7EAC90', illustration: 'dailyHomework' },
  'metronom-goz-sektirme': { tone: '#E8F6F8', accent: '#58B7CC', illustration: 'metronome' },
  'buyuyen-sonsuz-kareler': { tone: '#F4F1FA', accent: '#8D9BE6', illustration: 'squares' },
  'odaklan-ve-koseleri-gor': { tone: '#EEF6F2', accent: '#6FB7A0', illustration: 'periphery' },
  '13-nokta-goz-egzersizi': { tone: '#F2F6FB', accent: '#6F8AA2', illustration: 'nodes13' },
  'ponte-dairesel-goz-egzersizi': { tone: '#F5F6FA', accent: '#7D8BA4', illustration: 'ponte' },
  'dikey-zikzak-goz-egzersizi': { tone: '#F3F7FB', accent: '#738CA8', illustration: 'zigzagVertical' },
  'dikey-zikzak-2-goz-egzersizi': { tone: '#F4F8FB', accent: '#6D8BA7', illustration: 'zigzagVerticalLoop' },
  'yatay-zikzak-goz-egzersizi': { tone: '#F6F4FB', accent: '#7E88B6', illustration: 'zigzagHorizontal' },
  'yatay-zikzak-2-goz-egzersizi': { tone: '#F4F7FB', accent: '#6F87A8', illustration: 'zigzagHorizontalLoop' },
  'kum-saati-egzersizi': { tone: '#F8F5F1', accent: '#9D8B79', illustration: 'hourglass' },
  'goz-egzersizleri-dikey-zikzak': { tone: '#F0F7F7', accent: '#6F9FA6', illustration: 'guidedVertical' },
  'goz-egzersizleri-kolonlar': { tone: '#F4F6F0', accent: '#8D9B73', illustration: 'columns' },
  'goz-egzersizleri-yatay-zikzak': { tone: '#F6F5FA', accent: '#8E88B2', illustration: 'guidedHorizontal' },
};

export const FALLBACK_SECTIONS: ModeSection[] = [
  { id: 'goz-egzersizleri', title: 'Göz Egzersizleri', description: 'Görsel alanı ve satır takibini güçlendiren egzersizler.', order: 1 },
  { id: 'kelime-egzersizleri', title: 'Kelime Egzersizleri', description: 'Kelime ayırt etme ve anlam kurma çalışmaları.', order: 2 },
  { id: 'okuma-anlama-egzersizleri', title: 'Metin Egzersizleri', description: 'Metin okuma, anlama ve hız çalışmaları.', order: 3 },
  { id: 'dikkat-ve-odaklanma', title: 'Dikkat ve Odaklanma', description: 'Seçici dikkat, görsel tarama ve odak çalışmaları.', order: 4 },
  { id: 'serbest-okuma', title: 'Kütüphanem', description: 'Serbest okuma ve kişisel çalışma alanı.', order: 5 },
  { id: 'odevlerim', title: 'Ödevlerim', description: 'Günlük çalışma planları ve öğrenciye atanmış okuma ödevleri.', order: 6 },
];

type SectionVisual = {
  tone: string;
  accent: string;
  glow: string;
  illustration: 'eye' | 'words' | 'reading' | 'attention' | 'library' | 'homework';
};

const SECTION_VISUALS: Record<string, SectionVisual> = {
  'goz-egzersizleri': {
    tone: '#E8F7FF',
    accent: '#219EBC',
    glow: '#8BD3E6',
    illustration: 'eye',
  },
  'kelime-egzersizleri': {
    tone: '#FFF3D8',
    accent: '#F59E0B',
    glow: '#FBBF24',
    illustration: 'words',
  },
  'okuma-anlama-egzersizleri': {
    tone: '#EAF8EF',
    accent: '#2F9E6D',
    glow: '#74D99F',
    illustration: 'reading',
  },
  'dikkat-ve-odaklanma': {
    tone: '#F2EEFF',
    accent: '#7C5CE7',
    glow: '#A78BFA',
    illustration: 'attention',
  },
  'serbest-okuma': {
    tone: '#EAF4FF',
    accent: '#3478F6',
    glow: '#79A8FF',
    illustration: 'library',
  },
  odevlerim: {
    tone: '#FFF0EC',
    accent: '#F06449',
    glow: '#FFA38B',
    illustration: 'homework',
  },
};

export const FALLBACK_MODES: ModeConfig[] = [
  { id: 'gruplama', name: 'Gruplama', canonicalMode: 'grouping', sectionId: 'goz-egzersizleri', status: 'active', ...MODE_VISUALS.grouping },
  { id: 'golgeleme', name: 'Gölgeli Metin', canonicalMode: 'golgeleme', sectionId: 'goz-egzersizleri', status: 'active', legacyNames: ['Gölgeleme'], ...MODE_VISUALS.golgeleme },
  { id: 'bloklama', name: 'Bloklama', canonicalMode: 'bloklama', sectionId: 'goz-egzersizleri', status: 'active', ...MODE_VISUALS.bloklama },
  { id: 'blok-okuma', name: 'Blok Okuma', canonicalMode: 'blok-okuma', sectionId: 'kelime-egzersizleri', status: 'active', ...MODE_VISUALS['blok-okuma'] },
  { id: 'metronom-goz-sektirme', name: 'Metronom ve Göz Sektirme', canonicalMode: 'metronom-goz-sektirme', sectionId: 'goz-egzersizleri', status: 'hidden', ...MODE_VISUALS['metronom-goz-sektirme'] },
  { id: 'buyuyen-sonsuz-kareler', name: 'Büyüyen Sonsuz Kareler', canonicalMode: 'buyuyen-sonsuz-kareler', sectionId: 'goz-egzersizleri', status: 'active', ...MODE_VISUALS['buyuyen-sonsuz-kareler'] },
  { id: 'odaklan-ve-koseleri-gor', name: 'Odaklan ve Köşeleri Gör', canonicalMode: 'odaklan-ve-koseleri-gor', sectionId: 'goz-egzersizleri', status: 'hidden', ...MODE_VISUALS['odaklan-ve-koseleri-gor'] },
  { id: '13-nokta-goz-egzersizi', name: '13 Nokta', canonicalMode: '13-nokta-goz-egzersizi', sectionId: 'goz-egzersizleri', status: 'active', ...MODE_VISUALS['13-nokta-goz-egzersizi'] },
  { id: 'ponte-dairesel-goz-egzersizi', name: 'Dairesel', canonicalMode: 'ponte-dairesel-goz-egzersizi', sectionId: 'goz-egzersizleri', status: 'active', ...MODE_VISUALS['ponte-dairesel-goz-egzersizi'] },
  { id: 'kum-saati-egzersizi', name: 'Kum Saati Egzersizi', canonicalMode: 'kum-saati-egzersizi', sectionId: 'goz-egzersizleri', status: 'active', ...MODE_VISUALS['kum-saati-egzersizi'] },
  { id: 'dikey-zikzak-goz-egzersizi', name: 'Dikey Zikzak', canonicalMode: 'dikey-zikzak-goz-egzersizi', sectionId: 'goz-egzersizleri', status: 'hidden', ...MODE_VISUALS['dikey-zikzak-goz-egzersizi'] },
  { id: 'dikey-zikzak-2-goz-egzersizi', name: 'Dikey Zikzak 2', canonicalMode: 'dikey-zikzak-2-goz-egzersizi', sectionId: 'goz-egzersizleri', status: 'active', ...MODE_VISUALS['dikey-zikzak-2-goz-egzersizi'] },
  { id: 'yatay-zikzak-goz-egzersizi', name: 'Yatay Zikzak', canonicalMode: 'yatay-zikzak-goz-egzersizi', sectionId: 'goz-egzersizleri', status: 'active', ...MODE_VISUALS['yatay-zikzak-goz-egzersizi'] },
  { id: 'yatay-zikzak-2-goz-egzersizi', name: 'Yatay Zikzak 2', canonicalMode: 'yatay-zikzak-2-goz-egzersizi', sectionId: 'goz-egzersizleri', status: 'active', ...MODE_VISUALS['yatay-zikzak-2-goz-egzersizi'] },
  { id: 'goz-egzersizleri-dikey-zikzak', name: 'Dikey Zikzak', canonicalMode: 'goz-egzersizleri-dikey-zikzak', sectionId: 'goz-egzersizleri', status: 'hidden', ...MODE_VISUALS['goz-egzersizleri-dikey-zikzak'] },
  { id: 'goz-egzersizleri-kolonlar', name: 'Kolonlar', canonicalMode: 'goz-egzersizleri-kolonlar', sectionId: 'goz-egzersizleri', status: 'active', ...MODE_VISUALS['goz-egzersizleri-kolonlar'] },
  { id: 'goz-egzersizleri-yatay-zikzak', name: 'Yatay Zikzak', canonicalMode: 'goz-egzersizleri-yatay-zikzak', sectionId: 'goz-egzersizleri', status: 'hidden', ...MODE_VISUALS['goz-egzersizleri-yatay-zikzak'] },
  { id: 'metin-arama', name: 'Metin Arama', canonicalMode: 'metin-arama', sectionId: 'goz-egzersizleri', status: 'active', ...MODE_VISUALS['metin-arama'] },
  { id: 'kelime-arama', name: 'Kelime Arama', canonicalMode: 'kelime-arama', sectionId: 'goz-egzersizleri', status: 'active', ...MODE_VISUALS['kelime-arama'] },
  { id: 'benzer-kelimeler', name: 'Benzer Kelimeler', canonicalMode: 'benzer-kelimeler', sectionId: 'goz-egzersizleri', status: 'active', ...MODE_VISUALS['benzer-kelimeler'] },
  { id: 'takistoskop', name: 'Takistoskop', canonicalMode: 'takistoskop', sectionId: 'kelime-egzersizleri', status: 'active', legacyNames: ['Pilot Testi'], ...MODE_VISUALS.takistoskop },
  { id: 'bir-bakista-gorelim', name: 'Bir Bakışta Görelim', canonicalMode: 'bir-bakista-gorelim', sectionId: 'kelime-egzersizleri', status: 'active', ...MODE_VISUALS['bir-bakista-gorelim'] },
  { id: 'gozlerimizi-sektirerek-gorelim', name: 'Gözlerimizi Sektirerek Görelim', canonicalMode: 'gozlerimizi-sektirerek-gorelim', sectionId: 'kelime-egzersizleri', status: 'active', ...MODE_VISUALS['gozlerimizi-sektirerek-gorelim'] },
  { id: 'zit-anlam', name: 'Zıt Anlam', canonicalMode: 'zit-anlam', sectionId: 'kelime-egzersizleri', status: 'active', ...MODE_VISUALS['zit-anlam'] },
  { id: 'es-anlam', name: 'Eş Anlam', canonicalMode: 'es-anlam', sectionId: 'kelime-egzersizleri', status: 'active', ...MODE_VISUALS['es-anlam'] },
  { id: 'somut-anlam', name: 'Somut Anlam', canonicalMode: 'somut-anlam', sectionId: 'kelime-egzersizleri', status: 'active', ...MODE_VISUALS['somut-anlam'] },
  { id: 'okuma-anlama-seviye-testi', name: 'Okuma Anlama Seviye Testi', canonicalMode: 'okuma-anlama-seviye-testi', sectionId: 'okuma-anlama-egzersizleri', status: 'active', ...MODE_VISUALS['okuma-anlama-seviye-testi'] },
  { id: 'metin-okuma', name: 'Durağan Metin', canonicalMode: 'metin-okuma', sectionId: 'okuma-anlama-egzersizleri', status: 'active', legacyNames: ['Metin Okuma'], ...MODE_VISUALS['metin-okuma'] },
  { id: 'eksiltili-metin', name: 'Eksiltili Metin', canonicalMode: 'eksiltili-metin', sectionId: 'okuma-anlama-egzersizleri', status: 'active', ...MODE_VISUALS['eksiltili-metin'] },
  { id: 'sihirli-hiz', name: 'İşaretli Metin', canonicalMode: 'sihirli-hiz', sectionId: 'okuma-anlama-egzersizleri', status: 'active', ...MODE_VISUALS['sihirli-hiz'] },
  { id: 'hedef-yakalama', name: 'Akan Metin', canonicalMode: 'hedef-yakalama', sectionId: 'okuma-anlama-egzersizleri', status: 'active', legacyNames: ['Yakalama', 'Hedef Yakalama'], ...MODE_VISUALS['hedef-yakalama'] },
  { id: 'kaybolan-metin', name: 'Kaybolan Metin', canonicalMode: 'kaybolan-metin', sectionId: 'okuma-anlama-egzersizleri', status: 'active', ...MODE_VISUALS['kaybolan-metin'] },
  { id: 'multi-fokus', name: 'Multi Fokus', canonicalMode: 'multi-fokus', sectionId: 'kelime-egzersizleri', status: 'active', ...MODE_VISUALS['multi-fokus'] },
  { id: 'serbest-okuma-modu', name: 'Serbest Okuma', canonicalMode: 'serbest-okuma', sectionId: 'serbest-okuma', status: 'active', ...MODE_VISUALS['serbest-okuma'] },
  { id: 'dikkat-puzzle', name: 'Puzzle', canonicalMode: 'dikkat-puzzle', sectionId: 'dikkat-ve-odaklanma', status: 'active', ...MODE_VISUALS['dikkat-puzzle'] },
  { id: 'rengi-bul', name: 'Rengi Bul', canonicalMode: 'rengi-bul', sectionId: 'dikkat-ve-odaklanma', status: 'active', ...MODE_VISUALS['rengi-bul'] },
  { id: 'harf-sayma', name: 'Harf Sayma', canonicalMode: 'harf-sayma', sectionId: 'dikkat-ve-odaklanma', status: 'active', ...MODE_VISUALS['harf-sayma'] },
  { id: 'sayi-sayma', name: 'Sayı Sayma', canonicalMode: 'sayi-sayma', sectionId: 'dikkat-ve-odaklanma', status: 'active', ...MODE_VISUALS['sayi-sayma'] },
  { id: 'iki-resim-arasindaki-fark', name: 'İki Resim Arasındaki Fark', canonicalMode: 'iki-resim-arasindaki-fark', sectionId: 'dikkat-ve-odaklanma', status: 'active', ...MODE_VISUALS['iki-resim-arasindaki-fark'] },
  { id: 'gunluk-odevler', name: 'Günlük Çalışma Ödevleri', canonicalMode: 'gunluk-odevler', sectionId: 'odevlerim', status: 'active', ...MODE_VISUALS['gunluk-odevler'] },
];

export const GROUPING_SAMPLE_TEXT = `Öneri (Teklif) cümlesi, herhangi bir konuda birine teklifte bulunmak için kullanılır.
Hızlı okuma çalışmaları sırasında metni tek tek kelimeler yerine gruplar halinde görmek, göz hareketlerini azaltır ve kavramayı güçlendirir.
Bu egzersizde amaç satır boyunca açılan kelime bloklarını takip ederek okuma ritmini korumaktır.`;

export type GroupingSettings = {
  wordsPerGroup: number;
  characterCount?: number;
  wordsPerMinute: number;
  paceMs?: number;
  speedStep?: number;
  metronomeEnabled?: boolean;
  metronomeBpm?: number;
  stopwatchEnabled?: boolean;
  difficultyLevel: '5 ve 6. Sınıflar' | '7 ve 8. Sınıflar' | 'Lise' | 'Yetişkin' | 'İlkokul' | 'Ortaokul';
  fontSize: 'xkucuk' | 'kucuk' | 'orta' | 'buyuk' | 'xbuyuk';
  fontFamily: string;
  theme: 'light' | 'dark' | 'sepia';
  accentColor: string;
  enableCountdown: boolean;
};

export type GroupingSessionPrepareResponse = {
  sessionId: string;
  countdownSeconds: number;
  estimatedDurationSeconds: number;
  estimatedDurationLabel: string;
};

export type ShadowingPresentation = {
  blurStrategy?: string;
  focusWindowWordCount?: number;
  segments?: Array<{ index: number; text: string; blur: boolean }>;
  focusSequence?: Array<{ frameIndex: number; focusSegmentIndex: number; focusText: string }>;
};

export type IncompleteTextPresentation = {
  strategy?: string;
  blankCharacter?: string;
  maxDotsPerWord?: number;
  hiddenLetterCount?: number;
  wordCount?: number;
  displayText?: string;
  tokens?: Array<{
    type: 'word' | 'separator';
    index?: number;
    original?: string;
    text?: string;
    display?: string;
    missingLetters?: Array<{ charIndex: number; letter: string }>;
  }>;
};

export type SpeedReadingPresentation = {
  canonicalMode?: string;
  textType?: string;
  supportsSpeedControl?: boolean;
  blurNonFocusText?: boolean;
  shadowing?: ShadowingPresentation;
  incompleteText?: IncompleteTextPresentation;
  timing?: {
    type?: string;
    valueKey?: string;
    unit?: string;
    millisecondsPerWordFormula?: string;
    wordCount?: number;
  };
  markedText?: {
    strategy?: string;
    autoScroll?: boolean;
    showSideCursor?: boolean;
    highlightEffect?: string;
  };
  disappearingText?: {
    strategy?: string;
    direction?: string;
    unit?: string;
    selectableDisappearWordCounts?: number[];
    defaultDisappearWordCount?: number;
    autoScroll?: boolean;
  };
};

export type GroupingQuestion = {
  id: string;
  prompt: string;
  options: Array<{ key: 'A' | 'B' | 'C' | 'D' | 'E'; text: string }>;
  correctKey: 'A' | 'B' | 'C' | 'D' | 'E';
};

export type RecommendedStoryContent = {
  id: string;
  title: string;
  content: string;
  questions: GroupingQuestion[];
  mode?: string;
  displayMode?: string;
  difficultyLevel?: string | null;
  textType?: string | null;
  category?: {
    slug: string;
    name: string;
  } | null;
  presentation?: SpeedReadingPresentation | null;
  sourceFile?: {
    id?: string | number | null;
    name?: string | null;
    mimeType?: string | null;
    size?: number | null;
    url?: string | null;
    pageCount?: number | null;
    storageName?: string | null;
    pages?: Array<{
      id?: string | number | null;
      pageNumber?: number | null;
      imageUrl?: string | null;
    }> | null;
  } | null;
};

export type LibraryContentItem = {
  id: string;
  sourceKey: string;
  title: string;
  category?: {
    slug: string;
    name: string;
  } | null;
  textType?: string | null;
  difficultyLevel?: string | null;
  wordCount?: number;
  characterCount?: number;
  questionCount?: number;
  summary?: string;
  tags?: string[];
};

export type LibraryLevelGroup = {
  key: string;
  difficultyLevel: string;
  label: string;
  minimumRequired?: number;
  count: number;
  hasMinimum?: boolean;
  items: LibraryContentItem[];
};

export type MatchingWordApiItem = {
  leftWord: string;
  rightWord: string;
  level: number;
};

export type MatchingWordApiResponse = {
  type: 'synonym' | 'antonym';
  requestedLevel?: number | null;
  effectiveLevel?: number | null;
  items: MatchingWordApiItem[];
};

export const STORY_MODE_QUERY_BY_CANONICAL_MODE: Record<string, string> = {
  grouping: 'grouping',
  golgeleme: 'golgeleme',
  bloklama: 'blocking',
  'sihirli-hiz': 'sihirli-hiz',
  'metin-okuma': 'metin-okuma',
  'eksiltili-metin': 'eksiltili-metin',
  'anlama-calismalari': 'metin-okuma',
  'odaklanarak-okuma': 'metin-okuma',
  'serbest-okuma': 'serbest-okuma',
  'metin-arama': 'hedef-yakalama',
  'hedef-yakalama': 'hedef-yakalama',
  'kaybolan-metin': 'kaybolan-metin',
};

export const STORY_BASED_CANONICAL_MODES = new Set(Object.keys(STORY_MODE_QUERY_BY_CANONICAL_MODE));

export const normalizeRecommendedStoryContent = (raw: any): RecommendedStoryContent | null => {
  if (!raw || typeof raw !== 'object') return null;
  const normalizedDisplayMode = String(raw.displayMode || '').trim();
  const normalizedText = String(raw.content || '').trim();
  const allowsEmptyContent = normalizedDisplayMode === 'stored-file';
  if (!normalizedText && !allowsEmptyContent) return null;

  const normalizedDifficultyLevel = String(raw?.difficultyLevel || '').trim();
  const desiredOptionCount = ['Lise'].includes(normalizedDifficultyLevel) ? 5 : 4;
  const normalizedQuestions: GroupingQuestion[] = Array.isArray(raw.questions)
    ? raw.questions
      .map((question: any, index: number) => {
        const optionRecord = (question?.options && typeof question.options === 'object') ? question.options : {};
        const options = (['A', 'B', 'C', 'D', 'E'] as const)
          .map((key) => ({
            key,
            text: String(optionRecord[key] || '').trim(),
          }))
          .filter((option) => option.text);
        const normalizedCorrectKey = (['A', 'B', 'C', 'D', 'E'] as const).includes(question?.correctOption)
          ? question.correctOption
          : 'A';

        const normalizedOptions = options.slice(0, desiredOptionCount);
        if (desiredOptionCount === 5 && normalizedOptions.length === 4) {
          normalizedOptions.push({
            key: 'E',
            text: 'Metinde buna dair açık bir bilgi yer almamaktadır.',
          });
        }

        if (!question?.question || normalizedOptions.length === 0) return null;

        return {
          id: String(question.questionId || `q_${index + 1}`),
          prompt: String(question.question),
          options: normalizedOptions,
          correctKey: normalizedCorrectKey,
        };
      })
      .filter(Boolean) as GroupingQuestion[]
    : [];

  return {
    id: String(raw.id || ''),
    title: String(raw.title || ''),
    content: normalizedText,
    questions: normalizedQuestions,
    mode: String(raw.mode || ''),
    displayMode: normalizedDisplayMode,
    difficultyLevel: raw?.difficultyLevel || null,
    textType: raw?.textType || null,
    category: raw?.category || null,
    presentation: raw?.presentation || null,
    sourceFile: raw?.sourceFile && typeof raw.sourceFile === 'object'
      ? {
        id: raw.sourceFile.id ?? null,
        name: raw.sourceFile.name ?? null,
        mimeType: raw.sourceFile.mimeType ?? null,
        size: Number.isFinite(Number(raw.sourceFile.size)) ? Number(raw.sourceFile.size) : null,
        url: raw.sourceFile.url ?? null,
        pageCount: Number.isFinite(Number(raw.sourceFile.pageCount)) ? Number(raw.sourceFile.pageCount) : null,
        storageName: raw.sourceFile.storageName ?? null,
        pages: Array.isArray(raw.sourceFile.pages)
          ? raw.sourceFile.pages
            .filter((page: any) => page && typeof page === 'object')
            .map((page: any) => ({
              id: page.id ?? null,
              pageNumber: Number.isFinite(Number(page.pageNumber)) ? Number(page.pageNumber) : null,
              imageUrl: page.imageUrl ?? null,
            }))
          : null,
      }
      : null,
  };
};

export const normalizeModeKey = (value?: string | null) => String(value || '')
  .toLocaleLowerCase('tr-TR')
  .replace(/[ç]/g, 'c')
  .replace(/[ğ]/g, 'g')
  .replace(/[ı]/g, 'i')
  .replace(/[ö]/g, 'o')
  .replace(/[ş]/g, 's')
  .replace(/[ü]/g, 'u')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

export const getModeCanonicalKey = (mode?: ModeConfig | string | null) => {
  if (!mode) return '';
  if (typeof mode === 'string') return normalizeModeKey(mode);
  return normalizeModeKey(mode.canonicalMode || mode.id || mode.name);
};

export const getModeQueryValue = (mode?: ModeConfig | string | null) => {
  const canonicalKey = getModeCanonicalKey(mode);
  return STORY_MODE_QUERY_BY_CANONICAL_MODE[canonicalKey] || canonicalKey;
};

export const isStoryMode = (mode?: ModeConfig | string | null) => {
  return STORY_BASED_CANONICAL_MODES.has(getModeCanonicalKey(mode));
};

const buildModeConfigFromCatalogExercise = (exercise: ProgramCatalogExercise): ModeConfig => {
  const visuals = MODE_VISUALS[exercise.canonicalMode] || { tone: '#EDF3F5', accent: '#8EAAB8', illustration: 'text' };
  return {
    id: exercise.id,
    name: exercise.title,
    canonicalMode: exercise.canonicalMode,
    sectionId: exercise.sectionId,
    description: exercise.description,
    status: exercise.status,
    uiCapabilities: exercise.uiCapabilities || [],
    legacyNames: exercise.legacyNames || [],
    ...visuals,
  };
};

export const mapProgramCatalogToModes = (catalog?: ProgramCatalogResponse | null): { sections: ModeSection[]; modes: ModeConfig[] } => {
  if (!catalog?.sections?.length || !catalog?.exercises?.length) {
    return {
      sections: [],
      modes: [],
    };
  }

  const sections = catalog.sections.slice().sort((left, right) => (left.order || 0) - (right.order || 0));
  const modes = catalog.exercises
    .slice()
    .map((exercise) => buildModeConfigFromCatalogExercise(exercise));

  return { sections, modes };
};

export const fetchProgramCatalog = async () => {
  const response = await ApiService.getApi('speed-reading/program/catalog');
  return mapProgramCatalogToModes(response as ProgramCatalogResponse);
};

export const getMatchingWordTypeByModeName = (modeName?: string): 'synonym' | 'antonym' | null => {
  const normalized = getModeCanonicalKey(modeName);
  if (modeName === 'Eş Anlam' || normalized === 'es-anlam') return 'synonym';
  if (modeName === 'Zıt Anlam' || normalized === 'zit-anlam') return 'antonym';
  return null;
};

export const mapMatchingApiItemsToPairs = (items: MatchingWordApiItem[] = []): WordPair[] => {
  return items
    .map((item) => [String(item.leftWord || '').trim(), String(item.rightWord || '').trim()] as WordPair)
    .filter((pair) => pair[0] && pair[1]);
};

export const GROUPING_INFO_PARAGRAPHS = [
  'Gözün kelimelere bakarken yaptığı duraklamaları azaltır, tek bir bakışta bütünü görebilmeniz konusunda gözünüzü geliştirir. Aynı zamanda iç seslendirmeyi azaltmanızı sağlar.',
  'Gözlerinizle metin üzerinde açılan kısmı takip edeceksiniz ve kelime kelime bakmak yerine açılan kelime kalıplarının ortasına bakarak her açılan alandaki metni bütün olarak görmeye çalışacaksınız.',
];
export const BLOCKING_INFO_PARAGRAPHS = [
  'Bütünsel görme becerinizi geliştirir. Bu egzersiz, bir satırın yarısına kadar olan kelimeleri görmenizi sağlar.',
  'Belirli bir hızda gösterilen kelime öbeklerini bütün olarak odaklanarak görmeye çalışın, yakalaması zordur ama odağınızı geliştirmesi için çıkan kelimelere odaklanın.',
];
export const TEXT_READING_INFO_PARAGRAPHS = [
  'Güncel okuma hızınızı ve anlama seviyenizi farklı makaleler üzerinden ölçümlemenizi sağlar.',
  'Metni okuduktan sonra sürenizi not alır ve arkasından çıkan soruları doğru cevaplamaya çalışırsınız.',
];
export const PILOT_TEST_INFO_PARAGRAPHS = [
  'Kelimeleri iç seslendirme ihtiyacı olmadan anlamanızı sağlar. Gözün, kelimeye bakarken harcadığı zamanı azaltarak, seslendirmeden gördüğünüz anda kelimeyi anlamanızı sağlar.',
  'Yapman gereken ekranın orta kısmında çıkan kelimeleri görüp doğru bir şekilde yazıp CEVAPLA butonuna tıklamak.',
];
export const PERIPHERY_FOCUS_INFO_PARAGRAPHS = [
  'Gözün, görme alanını genişleterek her bir bakışta daha geniş bir alanın görülmesini sağlarken, okumaya ayrılan sürenin azalmasını sağlar.',
  'Yapman gereken ekranın rastgele kısmında çıkan kelimeleri görüp doğru bir şekilde yazıp CEVAPLA butonuna tıklamak.',
];
export const MATCHING_INFO_PARAGRAPHS = [
  'Kısa süre görünen iki kelime ya da biçimi tek bakışta karşılaştırma becerinizi geliştirir. Amaç, aynı olanla farklı olanı iç seslendirmeye takılmadan hızlıca ayırt etmektir.',
  'Ekranda yanıp sönen iki öğeyi dikkatle görün. Kelimeler görünürken bilgisayarda sol veya sağ ok ile, telefonda ise ekranın sol ya da sağ tarafına dokunarak karar verin.',
];
export const MULTIFOCUS_INFO_PARAGRAPHS = [
  'Bir metni okurken, yapmış olduğunuz anlama hatalarını azaltarak, bütünsel görme alanınızı genişletmenizi sağlar.',
  'Ekranda gördüğünüz 2 kelime aynı ise sağ yön tuşuna, farklı ise sol yön tuşuna tıklayınız.',
  'Eğer bilgisayardan çalışıyorsanız, bilgisayarınızın klavyesindeki sağ ve sol yön tuşlarını kullanarak çalışınız.',
  'Eğer tablet veya telefondan çalışıyorsanız da ekranınızda doğruysa sağa, yanlışsa sola dokunarak çalışınız.',
];
export const ANTONYM_INFO_PARAGRAPHS = [
  'Gösterilen kelimelerin, anlam bakımından zıt anlamlı olup olmadığını bulmanız beklenmektedir.',
  'Eğer bilgisayardan çalışıyorsanız, bilgisayarınızın klavyesindeki sağ ve sol yön tuşlarını kullanarak çalışınız.',
  'Eğer tablet veya telefondan çalışıyorsanız da ekranınızda doğruysa sağa, yanlışsa sola dokunarak çalışınız.',
];
export const SYNONYM_INFO_PARAGRAPHS = [
  'Gösterilen kelimelerin, anlam bakımından eş anlamlı olup olmadığını bulmanız beklenmektedir.',
  'Eğer bilgisayardan çalışıyorsanız, bilgisayarınızın klavyesindeki sağ ve sol yön tuşlarını kullanarak çalışınız.',
  'Eğer tablet veya telefondan çalışıyorsanız da ekranınızda doğruysa sağa, yanlışsa sola dokunarak çalışınız.',
];
export const CONCRETE_MEANING_INFO_PARAGRAPHS = [
  'Gösterilen kelimelerin, anlam bakımından somut anlamlı olup olmadığını bulmanız beklenmektedir.',
  'Eğer bilgisayardan çalışıyorsanız, bilgisayarınızın klavyesindeki sağ ve sol yön tuşlarını kullanarak çalışınız.',
  'Eğer tablet veya telefondan çalışıyorsanız da ekranınızda doğruysa sağa, yanlışsa sola dokunarak çalışınız.',
];
export const SHADOW_INFO_PARAGRAPHS = [
  'Birden çok kelimeyi bir arada görebilmeniz için geliştirilmiştir, bu egzersiz görüş açınızı genişletir ve iç seslendirmeyi azaltmanızı sağlar.',
  'Gözlerinizle metin üzerinde gölgelenerek gösterilen kelime öbeğini takip edip kelime kelime bakmak yerine açılan kelime öbeklerini bütün olarak görmeye çalışacaksınız.',
];
export const MAGIC_SPEED_INFO_PARAGRAPHS = [
  'Odaklanarak daha hızlı okuyabilmeniz için geliştirilmiştir, bu egzersiz iç seslendirmeyi azaltmanızı sağlar.',
  'Gözlerinizle metin üzerinde kayan kareyi takip ederek kelime öbeklerini daha akıcı biçimde okuyabilirsiniz.',
  'Bilgisayardan çalışıyorsanız sağ ve sol yön tuşlarını kullanarak kayan vurgunun yerini değiştirebilirsiniz.',
];

export const GROUPING_REVIEW_QUESTIONS: GroupingQuestion[] = [
  {
    id: 'q1',
    prompt: 'Öneri (Teklif) cümlesi hangi amaçla kullanılır?',
    options: [
      { key: 'A', text: 'Birine herhangi bir konuda teklifte bulunmak için' },
      { key: 'B', text: 'Bir olayın tarihini anlatmak için' },
      { key: 'C', text: 'Bir metni özetlemek için' },
      { key: 'D', text: 'Yazarın hayat hikayesini açıklamak için' },
    ],
    correctKey: 'A',
  },
  {
    id: 'q2',
    prompt: 'Metni gruplar halinde görmek hangi iki beceriyi destekler?',
    options: [
      { key: 'A', text: 'Ezberlemeyi ve yazım hızını' },
      { key: 'B', text: 'Göz hareketlerini azaltmayı ve kavramayı güçlendirmeyi' },
      { key: 'C', text: 'Sesli okumayı ve telaffuzu artırmayı' },
      { key: 'D', text: 'Sadece kelime sayısını artırmayı' },
    ],
    correctKey: 'B',
  },
  {
    id: 'q3',
    prompt: 'Bu egzersizdeki temel amaç nedir?',
    options: [
      { key: 'A', text: 'Her kelimeyi tek tek ezberlemek' },
      { key: 'B', text: 'Satır boyunca açılan kelime bloklarını takip ederek ritmi korumak' },
      { key: 'C', text: 'Metni sesli tekrar etmek' },
      { key: 'D', text: 'Sadece son cümleyi anlamak' },
    ],
    correctKey: 'B',
  },
];

export const TEXT_READING_SAMPLE_TEXT = `Türk edebiyatının önemli isimlerinden biri olan Şemsettin Sami, Osmanlı İmparatorluğu döneminde yaşamış bir yazar, dil bilimci ve siyaset adamıdır. 1850 yılında İstanbul'da doğan Şemsettin Sami, Türk edebiyatına pek çok eser kazandırmıştır. Şemsettin Sami, özellikle dil ve dil bilim alanında yaptığı çalışmalarla tanınmıştır. "Kamus-i Türki" adlı eseri, Türk dilinin ilk sözlüğüdür ve Türk dilinin gelişimine katkı sağlamıştır. Ayrıca, "Osmanlıca-Türkçe Sözlük" ve "Lugat-ı Osmani" adlı eserleriyle de Osmanlıca dilinin günümüze kadar korunmasına yardımcı olmuştur.

Şemsettin Sami'nin bu çalışmaları, Türk dilinin zenginleşmesi ve gelişmesi için önemli bir adım olmuştur. Yazarın edebiyat alanındaki en önemli eseri ise "Taaşşuk-ı Talat ve Fitnat" adlı romanıdır. Bu roman, Tanzimat döneminin ilk Türk romanıdır ve Türk edebiyatındaki roman geleneğinin temellerini atmıştır. Roman, Talat ve Fitnat adlı iki karakterin aşk hikayesini anlatır ve toplumsal değişimlerin yarattığı çatışmaları ele alır.

Şemsettin Sami'nin bu eseri, Türk edebiyatında bir dönüm noktası olarak kabul edilir. Şemsettin Sami, ayrıca siyasi hayatında da etkili olmuştur. II. Abdülhamid döneminde çeşitli devlet görevlerinde bulunmuş, Osmanlı Devleti'nin modernleşme çabalarına destek vermiş ve bu konuda çeşitli çalışmalar yapmıştır. Sonuç olarak, Şemsettin Sami, Türk edebiyatı ve dil bilimi alanında önemli bir isimdir.`;

export const TEXT_READING_REVIEW_QUESTIONS: GroupingQuestion[] = [
  {
    id: 'tr1',
    prompt: 'Şemsettin Sami en çok hangi alanlardaki çalışmalarıyla tanınmıştır?',
    options: [
      { key: 'A', text: 'Dil ve dil bilim alanındaki çalışmalarıyla' },
      { key: 'B', text: 'Mimarlık ve şehircilik çalışmalarıyla' },
      { key: 'C', text: 'Sadece tiyatro oyunlarıyla' },
      { key: 'D', text: 'Matematik ve astronomi araştırmalarıyla' },
    ],
    correctKey: 'A',
  },
  {
    id: 'tr2',
    prompt: '"Taaşşuk-ı Talat ve Fitnat" neden önemlidir?',
    options: [
      { key: 'A', text: 'İlk sözlük olduğu için' },
      { key: 'B', text: 'Tanzimat döneminin ilk Türk romanı olduğu için' },
      { key: 'C', text: 'Şiir türünde yazıldığı için' },
      { key: 'D', text: 'Yalnızca çocuklara hitap ettiği için' },
    ],
    correctKey: 'B',
  },
  {
    id: 'tr3',
    prompt: 'Metne göre Şemsettin Sami’nin etkisi hangi alanlarla sınırlı değildir?',
    options: [
      { key: 'A', text: 'Sadece roman yazarlığıyla sınırlıdır' },
      { key: 'B', text: 'Yalnızca sözlük hazırlamakla sınırlıdır' },
      { key: 'C', text: 'Edebiyatın yanında dil bilimi ve siyasi hayatta da etkilidir' },
      { key: 'D', text: 'Yalnızca çeviri faaliyetleriyle sınırlıdır' },
    ],
    correctKey: 'C',
  },
];

export const CAPTURE_INFO_PARAGRAPHS = [
  'Metni hızlı bir şekilde okuyabilmeniz için geliştirilmiştir.',
  'Okumaya ayrılan sürenin azalmasını sağlar.',
  'Satırların üzeri kapanmadan kelimeleri yakalamaya çalışın.',
];

export const CAPTURE_SAMPLE_TEXT = `Miken İmparatorluğu, antik dönemde Ege Denizi'nin doğusunda bulunan bir Yunan medeniyetidir. MÖ 1600 ile MÖ 1100 yılları arasında varlığını sürdüren bu imparatorluk, tarih boyunca önemli bir kültürel ve ticaret merkezi olmuştur. Miken İmparatorluğu'nun başkenti, bugün Yunanistan'ın Peloponez Yarımadası'nda yer alan Miken şehriydi. İmparatorluğun kontrolü altında geniş bir alan bulunmaktaydı ve toprakları Yunanistan'ın yanı sıra Ege Denizi'ndeki birçok adayı da kapsıyordu. Miken İmparatorluğu'nun ekonomisi tarım, hayvancılık, madencilik ve deniz ticareti üzerine kurulmuştu. Özellikle zeytinyağı, seramik ürünler ve metaller ticarette önemli rol oynuyordu. Bu ticaret ağının genişlemesi, Miken kültürünün diğer uygarlıklarla etkileşimini artırmıştır.

İmparatorluğun en parlak dönemi, MÖ 14. ve 13. yüzyıllara denk gelir. Bu dönemde, Miken Yunanistan'ın en güçlü krallıklarından biriydi ve Anadolu'nun batısında bulunan Hitit İmparatorluğu ile ticari ilişkiler kurmuştu. Miken İmparatorluğu'nun zirvesinde, Miken sarayları mimari açıdan göz alıcıydı ve saraylarında büyük miktarda altın ve gümüş bulunmaktaydı. Ancak MÖ 12. yüzyılda, Miken İmparatorluğu çeşitli iç ve dış tehditlerle karşı karşıya kaldı. İç isyanlar, dış saldırılar ve deniz kavimleri olarak bilinen göçebe kavimlerin akınları, imparatorluğun zayıflamasına ve sonunda çökmesine neden oldu. MÖ 12. yüzyılın sonlarına doğru, Miken İmparatorluğu'nun hakimiyeti giderek azaldı ve sonunda tamamen ortadan kayboldu. Bugün, Miken İmparatorluğu'nun kalıntıları, Yunanistan'ın antik mirasının önemli bir parçasıdır.`;

export const CAPTURE_REVIEW_QUESTION: GroupingQuestion = {
  id: 'capture_q1',
  prompt: 'Metne göre Miken İmparatorluğu\'nun geniş bir ekonomisi ve ticaret yolları bulunmaktadır. Aşağıdakilerden hangisi Miken İmparatorluğu\'nun ticaretini yaptığı ürünler arasında yer almaz?',
  options: [
    { key: 'A', text: 'Zeytinyağı' },
    { key: 'B', text: 'Seramik ürünler' },
    { key: 'C', text: 'Altın ve bakır' },
    { key: 'D', text: 'Metaller' },
  ],
  correctKey: 'C',
};

export type PilotSettings = {
  wordsPerMinute: number;
  paceMs?: number;
  speedStep?: number;
  metronomeEnabled?: boolean;
  metronomeBpm?: number;
  stopwatchEnabled?: boolean;
  level: number;
  fontSize: GroupingSettings['fontSize'];
  fontFamily: string;
  accentColor: string;
  enableCountdown: boolean;
};

export type MatchingRoundResult = {
  left: string;
  right: string;
  isMatch: boolean;
  userChoice: boolean | null;
  isCorrect: boolean;
};
export type MatchingPairGeneratorOptions = {
  usedPairKeys?: Set<string>;
  usedWordCounts?: Record<string, number>;
  forcedMatch?: boolean;
  dynamicWords?: string[];
};

export type PeripheryPosition = React.CSSProperties;
export type WordPair = [string, string];
export type ConcreteMeaningWord = {
  word: string;
  isConcrete: boolean;
};

export type ConcreteMeaningRoundResult = {
  word: string;
  isConcrete: boolean;
  userChoice: boolean | null;
  isCorrect: boolean;
};

export const PILOT_TEST_LEVEL_WORDS: Record<number, string[]> = {
  1: ['dal', 'kol', 'gül', 'ses', 'buz', 'saç', 'diş', 'kaş'],
  2: ['kuyu', 'kalp', 'deri', 'para', 'bina', 'ilaç', 'inek', 'ipek'],
  3: ['bulut', 'çanta', 'silgi', 'kürek', 'limon', 'meyve', 'incir', 'koyun'],
  4: ['gözlük', 'fındık', 'yıldız', 'kartal', 'çiftçi', 'doktor', 'orkide', 'salata'],
  5: ['çekirge', 'papatya', 'patates', 'domates', 'şeftali', 'kavanoz', 'tencere', 'kırmızı'],
  6: ['salıncak', 'dondurma', 'merdiven', 'sarımsak', 'bisiklet', 'çilekler', 'kütükler', 'yaprakla'],
  7: ['sınıfımız', 'ormanlara', 'kelebekli', 'çiçekleri', 'arkadaşım', 'öğrenciyi', 'kaplanlar', 'defterime'],
  8: ['arkadaşlar', 'uçurtmalar', 'portakallı', 'gökyüzünde', 'denizcilik', 'ormanlarda', 'fındıkları', 'çekirgeler'],
  9: ['çiçeklerden', 'pencerelere', 'doktorlarla', 'kütüphaneci', 'öğretmenler', 'ayakkabıcım', 'uçurtmamızı', 'denizaltına'],
  10: ['yağmurluğumu', 'kelebeklerin', 'kitaplığımız', 'denizlerimiz', 'öğretmenimiz', 'bilgisayarım', 'helikopterin', 'kütüphanemiz'],
  11: ['portakalların', 'yıldızlarımın', 'sınıfımızdaki', 'defterlerimiz', 'bahçıvanımızı', 'pencerelerden', 'ormanlarımıza', 'çiçeklerimize'],
  12: ['ayakkabılarını', 'gökkuşağınızda', 'denizaltımızda', 'kalemtıraşımız', 'portakallarımı', 'yıldızlarımızı', 'sınıflarımızda', 'defterlerimize'],
  13: ['uçurtmalarımızı', 'gökkuşaklarımız', 'denizaltılarına', 'kalemtıraşlarım', 'portakallarımız', 'yıldızlarımızda', 'sınıflarımızdan', 'defterlerimizde'],
};

export const MULTIFOCUS_LEVEL_WORDS: Record<number, string[]> = {
  1: ['kek', 'cep', 'göl', 'tuz', 'kaz', 'koç', 'kap', 'çam'],
  2: ['ateş', 'ağaç', 'adam', 'ayna', 'ayva', 'arpa', 'ekin', 'erik'],
  3: ['burun', 'çorap', 'sepet', 'tavuk', 'ceviz', 'kuzey', 'güneş', 'kavun'],
  4: ['makine', 'tarçın', 'kelime', 'mektup', 'balina', 'yorgan', 'yastık', 'kayısı'],
  5: ['uçurtma', 'bahçesi', 'anahtar', 'manzara', 'sabahçı', 'deneyim', 'bereket', 'kestane'],
  6: ['sınıflar', 'balıkçıl', 'kaleciye', 'böcekler', 'kalemlik', 'pazarcık', 'kuşlarla', 'sepetler'],
  7: ['pencereye', 'bahçeleri', 'kitaplığa', 'tavşanlar', 'karıncalı', 'denizlere', 'bayraklar', 'sıralarda'],
  8: ['okulumuzda', 'kalemimizi', 'çantamızda', 'sıramızdan', 'saksımızda', 'kuşlarımız', 'bayrağımız', 'yolumuzdan'],
  9: ['gökkuşağını', 'bilgisayarı', 'kaplumbağam', 'helikopteri', 'kalemtıraşı', 'arkadaşımız', 'sıralarımız', 'öğrencilere'],
  10: ['öğrencilerim', 'kitaplarımız', 'defterlerimi', 'kalemlerimiz', 'çantalarımız', 'tahtalarımız', 'saksılarımız', 'kuşlarımızın'],
  11: ['tavşanlarımın', 'yağmurluğunuz', 'kitaplarımızı', 'öğretmenlerim', 'uçurtmalarımı', 'bayraklarımız', 'yollarımızdan', 'evlerimizdeki'],
  12: ['pencerelerimiz', 'ormanlarımızda', 'çiçeklerimizin', 'tavşanlarımıza', 'yağmurluklarım', 'kitaplarımızda', 'denizlerimizde', 'kedilerimizden'],
  13: ['pencerelerimize', 'ormanlarımızdan', 'çiçeklerimizden', 'tavşanlarımızın', 'kelebeklerimize', 'kitaplarımızdan', 'denizlerimizden', 'kaplanlarımızın'],
};

export const ANTONYM_LEVEL_PAIRS: Record<number, WordPair[]> = {
  1: [['büyük', 'küçük'], ['açık', 'kapalı'], ['genç', 'yaşlı'], ['ince', 'kalın'], ['alt', 'üst'], ['az', 'çok']],
  2: [['uzun', 'kısa'], ['erken', 'geç'], ['hızlı', 'yavaş'], ['zor', 'kolay'], ['tok', 'aç'], ['sıcak', 'soğuk']],
  3: [['yakın', 'uzak'], ['doğru', 'yanlış'], ['karanlık', 'aydınlık'], ['sert', 'yumuşak'], ['dolu', 'boş'], ['eski', 'yeni']],
  4: [['giriş', 'çıkış'], ['iniş', 'çıkış'], ['kazanç', 'zarar'], ['barış', 'savaş'], ['mutlu', 'üzgün'], ['önce', 'sonra']],
  5: [['başlangıç', 'bitiş'], ['iyilik', 'kötülük'], ['düzenli', 'dağınık'], ['yukarı', 'aşağı'], ['artı', 'eksi'], ['varlıklı', 'yoksul']],
  6: [['cesur', 'korkak'], ['güçlü', 'zayıf'], ['temiz', 'kirli'], ['saygılı', 'saygısız'], ['canlı', 'cansız'], ['dolaylı', 'doğrudan']],
  7: [['şeffaf', 'opak'], ['gizli', 'açık'], ['kalıcı', 'geçici'], ['gerçek', 'sahte'], ['aktif', 'pasif'], ['sertlik', 'yumuşaklık']],
  8: [['olumlu', 'olumsuz'], ['soyut', 'somut'], ['içten', 'yapay'], ['verimli', 'verimsiz'], ['adil', 'adaletsiz'], ['uyumlu', 'uyumsuz']],
  9: [['başarılı', 'başarısız'], ['bilinçli', 'bilinçsiz'], ['avantaj', 'dezavantaj'], ['özgün', 'taklit'], ['sakin', 'gergin'], ['nitelikli', 'niteliksiz']],
  10: [['tutarlı', 'tutarsız'], ['bağımlı', 'bağımsız'], ['nesnel', 'öznel'], ['modern', 'geleneksel'], ['merkezî', 'yerel'], ['esnek', 'katı']],
  11: [['iyileşme', 'bozulma'], ['ilerleme', 'gerileme'], ['özgürlük', 'esaret'], ['çoğunluk', 'azınlık'], ['aşinalık', 'yabancılık'], ['olgunluk', 'acemilik']],
  12: [['sadelik', 'karmaşıklık'], ['derinlik', 'yüzeysellik'], ['belirlilik', 'belirsizlik'], ['istikrar', 'istikrarsızlık'], ['uygarlık', 'ilkellik'], ['yakınsama', 'ıraksama']],
  13: [['nesnellik', 'öznellik'], ['zorunluluk', 'özgürlük'], ['gerçekçilik', 'hayalcilik'], ['rasyonellik', 'duygusallık'], ['bütünsellik', 'parçalılık'], ['merkezileşme', 'dağınıklık']],
};

export const SYNONYM_LEVEL_PAIRS: Record<number, WordPair[]> = {
  1: [['ilave', 'ek'], ['sene', 'yıl'], ['yanıt', 'cevap'], ['misafir', 'konuk'], ['beyaz', 'ak'], ['siyah', 'kara']],
  2: [['fakir', 'yoksul'], ['zengin', 'varlıklı'], ['mutlu', 'mesut'], ['doktor', 'hekim'], ['şehir', 'kent'], ['ulus', 'millet']],
  3: [['sözcük', 'kelime'], ['öykü', 'hikaye'], ['yetenek', 'beceri'], ['güz', 'sonbahar'], ['öğrenci', 'talebe'], ['yürek', 'kalp']],
  4: [['anı', 'hatıra'], ['ikaz', 'uyarı'], ['görev', 'vazife'], ['sınav', 'imtihan'], ['ömür', 'hayat'], ['eser', 'yapıt']],
  5: [['olanak', 'imkan'], ['mesele', 'sorun'], ['özen', 'itina'], ['yöntem', 'metot'], ['amaç', 'hedef'], ['sebep', 'neden']],
  6: [['akıl', 'us'], ['özgün', 'orijinal'], ['kanıt', 'delil'], ['zorunlu', 'mecburi'], ['ihtimal', 'olasılık'], ['yoksun', 'mahrum']],
  7: [['girişim', 'teşebbüs'], ['samimi', 'içten'], ['gayret', 'çaba'], ['barınak', 'sığınak'], ['tasarı', 'plan'], ['kapsam', 'içerik']],
  8: [['bağımsız', 'müstakil'], ['özlem', 'hasret'], ['uygar', 'medeni'], ['evrak', 'belge'], ['tamir', 'onarım'], ['armağan', 'hediye']],
  9: [['yorum', 'değerlendirme'], ['öngörü', 'tahmin'], ['özveri', 'fedakarlık'], ['saygın', 'itibarlı'], ['başvuru', 'müracaat'], ['özgür', 'hür']],
  10: [['varsayım', 'hipotez'], ['denge', 'istikrar'], ['bakış', 'perspektif'], ['uygulama', 'pratik'], ['zorlayıcı', 'çetin'], ['gerçek', 'hakikat']],
  11: [['kavramsal', 'mefhumsal'], ['belirgin', 'bariz'], ['gelenek', 'anane'], ['eğilim', 'temayül'], ['çözümleme', 'analiz'], ['özet', 'hulasa']],
  12: [['öznel', 'subjektif'], ['nesnel', 'objektif'], ['varsımsal', 'farazi'], ['kuram', 'teori'], ['dönüşüm', 'transformasyon'], ['tutarlılık', 'istikrar']],
  13: [['yetkin', 'mükemmel'], ['münazara', 'tartışma'], ['tetkik', 'inceleme'], ['mukayese', 'karşılaştırma'], ['tevazu', 'alçakgönüllülük'], ['müspet', 'olumlu']],
};

export const CONCRETE_MEANING_LEVEL_WORDS: Record<number, ConcreteMeaningWord[]> = {
  1: [
    { word: 'masa', isConcrete: true },
    { word: 'taş', isConcrete: true },
    { word: 'su', isConcrete: true },
    { word: 'kapı', isConcrete: true },
    { word: 'sevgi', isConcrete: false },
    { word: 'umut', isConcrete: false },
    { word: 'neşe', isConcrete: false },
    { word: 'öfke', isConcrete: false },
  ],
  2: [
    { word: 'çiçek', isConcrete: true },
    { word: 'kalem', isConcrete: true },
    { word: 'kuş', isConcrete: true },
    { word: 'bulut', isConcrete: true },
    { word: 'dostluk', isConcrete: false },
    { word: 'özlem', isConcrete: false },
    { word: 'korku', isConcrete: false },
    { word: 'cesaret', isConcrete: false },
  ],
  3: [
    { word: 'telefon', isConcrete: true },
    { word: 'ağaç', isConcrete: true },
    { word: 'deniz', isConcrete: true },
    { word: 'bardak', isConcrete: true },
    { word: 'özgürlük', isConcrete: false },
    { word: 'merak', isConcrete: false },
    { word: 'mutluluk', isConcrete: false },
    { word: 'saygı', isConcrete: false },
  ],
  4: [
    { word: 'tren', isConcrete: true },
    { word: 'ceket', isConcrete: true },
    { word: 'dağ', isConcrete: true },
    { word: 'lamba', isConcrete: true },
    { word: 'adalet', isConcrete: false },
    { word: 'yetenek', isConcrete: false },
    { word: 'başarı', isConcrete: false },
    { word: 'hüzün', isConcrete: false },
  ],
  5: [
    { word: 'mikroskop', isConcrete: true },
    { word: 'gemi', isConcrete: true },
    { word: 'pencere', isConcrete: true },
    { word: 'merdiven', isConcrete: true },
    { word: 'bilgelik', isConcrete: false },
    { word: 'sorumluluk', isConcrete: false },
    { word: 'dikkat', isConcrete: false },
    { word: 'sabır', isConcrete: false },
  ],
  6: [
    { word: 'heykel', isConcrete: true },
    { word: 'kamera', isConcrete: true },
    { word: 'orman', isConcrete: true },
    { word: 'bileklik', isConcrete: true },
    { word: 'fedakarlık', isConcrete: false },
    { word: 'kararlılık', isConcrete: false },
    { word: 'endişe', isConcrete: false },
    { word: 'olgunluk', isConcrete: false },
  ],
  7: [
    { word: 'dürbün', isConcrete: true },
    { word: 'fener', isConcrete: true },
    { word: 'köprü', isConcrete: true },
    { word: 'keman', isConcrete: true },
    { word: 'heyecan', isConcrete: false },
    { word: 'arkadaşlık', isConcrete: false },
    { word: 'üzüntü', isConcrete: false },
    { word: 'gurur', isConcrete: false },
  ],
  8: [
    { word: 'pusula', isConcrete: true },
    { word: 'yağmurluk', isConcrete: true },
    { word: 'iskele', isConcrete: true },
    { word: 'bavul', isConcrete: true },
    { word: 'dürüstlük', isConcrete: false },
    { word: 'güven', isConcrete: false },
    { word: 'yalnızlık', isConcrete: false },
    { word: 'merhamet', isConcrete: false },
  ],
  9: [
    { word: 'mikrofon', isConcrete: true },
    { word: 'ansiklopedi', isConcrete: true },
    { word: 'teleskop', isConcrete: true },
    { word: 'kütüphane', isConcrete: true },
    { word: 'özveri', isConcrete: false },
    { word: 'hoşgörü', isConcrete: false },
    { word: 'nezaket', isConcrete: false },
    { word: 'sabırlılık', isConcrete: false },
  ],
  10: [
    { word: 'laboratuvar', isConcrete: true },
    { word: 'projektör', isConcrete: true },
    { word: 'terazi', isConcrete: true },
    { word: 'vitrin', isConcrete: true },
    { word: 'saygınlık', isConcrete: false },
    { word: 'tutarlılık', isConcrete: false },
    { word: 'şaşkınlık', isConcrete: false },
    { word: 'yaratıcılık', isConcrete: false },
  ],
  11: [
    { word: 'stetoskop', isConcrete: true },
    { word: 'kervansaray', isConcrete: true },
    { word: 'avize', isConcrete: true },
    { word: 'çekiç', isConcrete: true },
    { word: 'azim', isConcrete: false },
    { word: 'zorluk', isConcrete: false },
    { word: 'iyimserlik', isConcrete: false },
    { word: 'sadakat', isConcrete: false },
  ],
  12: [
    { word: 'pergel', isConcrete: true },
    { word: 'tribün', isConcrete: true },
    { word: 'vinç', isConcrete: true },
    { word: 'madalyon', isConcrete: true },
    { word: 'kıskançlık', isConcrete: false },
    { word: 'pişmanlık', isConcrete: false },
    { word: 'karmaşa', isConcrete: false },
    { word: 'kararsızlık', isConcrete: false },
  ],
  13: [
    { word: 'mıknatıs', isConcrete: true },
    { word: 'gözlemevi', isConcrete: true },
    { word: 'periskop', isConcrete: true },
    { word: 'mühür', isConcrete: true },
    { word: 'mantık', isConcrete: false },
    { word: 'bilinç', isConcrete: false },
    { word: 'özgüven', isConcrete: false },
    { word: 'hayal', isConcrete: false },
  ],
};

export const DEFAULT_ACCENT_HEX = '#134657';

export const ACCENT_COLOR_OPTIONS = [
  { label: 'Varsayılan', value: 'default', hex: DEFAULT_ACCENT_HEX },
  { label: 'Kırmızı', value: 'red', hex: '#ff0000' },
  { label: 'Mavi', value: 'blue', hex: '#00bcd4' },
  { label: 'Sarı', value: 'yellow', hex: '#edd92a' },
  { label: 'Pembe', value: 'pink', hex: '#e91e63' },
  { label: 'Yeşil', value: 'green', hex: '#4caf50' },
] as const;

export const METRONOME_BPM_OPTIONS = [40, 50, 60, 100, 120, 150, 180] as const;
export const METRONOME_AUDIO_SRC = '/metronom.mp3';

export const normalizeMetronomeBpm = (value?: number | string | null, fallback = 60) => {
  const numericValue = Number(value);
  const numericFallback = Number(fallback);
  const target = Number.isFinite(numericValue) ? numericValue : numericFallback;

  let closest = METRONOME_BPM_OPTIONS[0] as number;
  METRONOME_BPM_OPTIONS.forEach((option) => {
    if (Math.abs(option - target) < Math.abs(closest - target)) {
      closest = option;
    }
  });
  return closest;
};

export const getAdjacentMetronomeBpm = (value: number, direction: -1 | 1) => {
  const normalized = normalizeMetronomeBpm(value);
  const currentIndex = METRONOME_BPM_OPTIONS.findIndex((option) => option === normalized);
  const nextIndex = Math.min(METRONOME_BPM_OPTIONS.length - 1, Math.max(0, currentIndex + direction));
  return METRONOME_BPM_OPTIONS[nextIndex];
};

export const getMetronomeIntervalMs = (value?: number | string | null) => Math.round(60000 / normalizeMetronomeBpm(value));

export const FONT_SIZE_OPTIONS: Array<{ label: string; value: GroupingSettings['fontSize']; preview: string }> = [
  { label: 'Aa', value: 'xkucuk', preview: 'text-[10px]' },
  { label: 'Aa', value: 'kucuk', preview: 'text-xs' },
  { label: 'Aa', value: 'orta', preview: 'text-sm' },
  { label: 'Aa', value: 'buyuk', preview: 'text-base' },
  { label: 'Aa', value: 'xbuyuk', preview: 'text-lg' },
];

export const FONT_FAMILY_OPTIONS = [
  { label: 'GoogleSans', value: 'GoogleSans', css: 'GoogleSans, Inter, Arial, sans-serif' },
  { label: 'Helvetica', value: 'Helvetica', css: 'Helvetica, Arial, sans-serif' },
  { label: 'Palatino', value: 'Palatino', css: '"Palatino Linotype", Palatino, Georgia, serif' },
  { label: 'OpenDyslexic', value: 'OpenDyslexic', css: '"OpenDyslexic", "Comic Sans MS", sans-serif' },
];

export const DIFFICULTY_OPTIONS: GroupingSettings['difficultyLevel'][] = ['İlkokul', 'Ortaokul', 'Lise'];
export const FONT_SIZE_LABELS: Record<GroupingSettings['fontSize'], string> = {
  xkucuk: 'Çok Küçük',
  kucuk: 'Küçük',
  orta: 'Orta',
  buyuk: 'Büyük',
  xbuyuk: 'Çok Büyük',
};

export const THEME_PRIMARY_CSS = 'var(--theme-primary)';
export const THEME_PRIMARY_SOFT_CSS = 'color-mix(in srgb, var(--theme-primary) 14%, white)';

export const TEXT_READING_FONT_SIZE_CLASS: Record<GroupingSettings['fontSize'], string> = {
  xkucuk: 'text-[14px] leading-[1.55]',
  kucuk: 'text-[18px] leading-[1.6]',
  orta: 'text-[22px] leading-[1.6]',
  buyuk: 'text-[26px] leading-[1.65]',
  xbuyuk: 'text-[30px] leading-[1.7]',
};

export const PILOT_WORD_BASE_SIZE: Record<GroupingSettings['fontSize'], number> = {
  xkucuk: 30,
  kucuk: 38,
  orta: 48,
  buyuk: 56,
  xbuyuk: 64,
};

export const PILOT_WORD_MIN_SIZE: Record<GroupingSettings['fontSize'], number> = {
  xkucuk: 14,
  kucuk: 22,
  orta: 26,
  buyuk: 30,
  xbuyuk: 34,
};

export const formatClock = (seconds: number) => {
  const safe = Math.max(0, Math.floor(seconds));
  const minute = Math.floor(safe / 60);
  const second = safe % 60;
  return `${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
};

export const GROUPING_DIFFICULTY_MULTIPLIER: Record<GroupingSettings['difficultyLevel'], number> = {
  '5 ve 6. Sınıflar': 0.88,
  '7 ve 8. Sınıflar': 1,
  Lise: 1.12,
  Yetişkin: 1.24,
  'İlkokul': 0.88,
  'Ortaokul': 1,
};

export const normalizeDifficultyLevel = (value?: string | null): GroupingSettings['difficultyLevel'] => {
  const str = String(value || '').trim().toLowerCase();
  switch (str) {
    case 'ilkokul':
    case 'kolay':
    case '5 ve 6. sınıflar':
    case '5-6':
      return 'İlkokul';
    case 'ortaokul':
    case 'orta':
    case '7 ve 8. sınıflar':
    case '7-8':
      return 'Ortaokul';
    case 'lise':
    case 'zor':
    case 'yetişkin':
    case 'yetiskin':
      return 'Lise';
    default:
      if (str.includes('ilkokul')) return 'İlkokul';
      if (str.includes('ortaokul')) return 'Ortaokul';
      if (str.includes('lise')) return 'Lise';
      return 'Ortaokul';
  }
};

export const normalizeAccentColor = (value?: string | null): string => {
  const matched = ACCENT_COLOR_OPTIONS.find((item) => item.value === value);
  return matched ? matched.value : 'default';
};

export const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

export const resolveAccentColor = (value?: string | null) => {
  if (!value || value === 'default') return DEFAULT_ACCENT_HEX;
  return ACCENT_COLOR_OPTIONS.find((item) => item.value === value)?.hex || DEFAULT_ACCENT_HEX;
};

export const SHARED_EXERCISE_SETTINGS_STORAGE_KEY = 'speed-reading.shared-exercise-settings.v1';
export const MODES_GRID_VIEW_STORAGE_KEY = 'speed-reading.modes-grid-view.v3';
export const MODES_GRID_LIST_COLUMNS_STORAGE_KEY = 'speed-reading.modes-grid-list-columns.v1';
export const SHARED_EXERCISE_SETTING_KEYS = [
  'wordsPerMinute',
  'speedStep',
  'wordsPerGroup',
  'characterCount',
  'exerciseDurationSeconds',
  'jumpDurationMs',
  'pointCount',
  'markCorners',
  'animationEnabled',
  'showFocusPoint',
  'showLetters',
  'letterCount',
  'letterSizePx',
  'selectedLetters',
  'metronomeEnabled',
  'metronomeBpm',
  'stopwatchEnabled',
  'fontSize',
  'fontFamily',
  'theme',
  'accentColor',
  'enableCountdown',
  'incompleteTextMaxDots',
  'wordGlanceWordCount',
] as const;

export type SharedExerciseSettings = Partial<Record<(typeof SHARED_EXERCISE_SETTING_KEYS)[number], unknown>>;
export const GROUPING_SETTING_KEYS = [
  'wordsPerGroup',
  'characterCount',
  'wordsPerMinute',
  'difficultyLevel',
  'fontSize',
  'fontFamily',
  'theme',
  'accentColor',
  'enableCountdown',
] as const;
let sharedSettingsHydrationPromise: Promise<void> | null = null;
let sharedSettingsPersistTimer: number | null = null;
let groupingBootstrapInFlight: Promise<[any, any]> | null = null;

export const getSharedExerciseSettings = (): SharedExerciseSettings => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(SHARED_EXERCISE_SETTINGS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    console.error('Ortak egzersiz ayarları okunamadı:', error);
    return {};
  }
};

export const applySharedExerciseSettings = <T extends Record<string, any>>(settings: T): T => {
  const shared = getSharedExerciseSettings();
  const sharedEntries = Object.entries(shared).filter(([key]) => key in settings);
  if (sharedEntries.length === 0) return settings;
  return {
    ...settings,
    ...Object.fromEntries(sharedEntries),
  };
};

export const toGroupingApiSettings = <T extends Record<string, any>>(settings: T) => (
  Object.fromEntries(
    Object.entries(settings).filter(([key]) => GROUPING_SETTING_KEYS.includes(key as any))
  ) as Pick<T, (typeof GROUPING_SETTING_KEYS)[number]>
);

export const persistSharedExerciseSettings = (settings: Record<string, any>) => {
  if (typeof window === 'undefined') return;
  try {
    const current = getSharedExerciseSettings();
    const next: SharedExerciseSettings = { ...current };
    SHARED_EXERCISE_SETTING_KEYS.forEach((key) => {
      if (key in settings) {
        next[key] = settings[key];
      }
    });
    if (JSON.stringify(current) === JSON.stringify(next)) {
      return;
    }
    window.localStorage.setItem(SHARED_EXERCISE_SETTINGS_STORAGE_KEY, JSON.stringify(next));

    if (sharedSettingsPersistTimer) {
      window.clearTimeout(sharedSettingsPersistTimer);
    }
    sharedSettingsPersistTimer = window.setTimeout(() => {
      ApiService.putApi('speed-reading/exercises/settings', next).catch((error: any) => {
        console.error('Ortak egzersiz ayarları sunucuya kaydedilemedi:', error);
      });
    }, 180);
  } catch (error) {
    console.error('Ortak egzersiz ayarları kaydedilemedi:', error);
  }
};

export const hydrateSharedExerciseSettingsFromServer = async () => {
  if (typeof window === 'undefined') return;
  if (!sharedSettingsHydrationPromise) {
    sharedSettingsHydrationPromise = (async () => {
      try {
        const serverSettings = await ApiService.getApi('speed-reading/exercises/settings');
        if (!serverSettings || typeof serverSettings !== 'object') return;
        const current = getSharedExerciseSettings();
        const merged = {
          ...current,
          ...Object.fromEntries(
            Object.entries(serverSettings).filter(([key]) => SHARED_EXERCISE_SETTING_KEYS.includes(key as any))
          ),
        };
        window.localStorage.setItem(SHARED_EXERCISE_SETTINGS_STORAGE_KEY, JSON.stringify(merged));
      } catch (error) {
        console.error('Ortak egzersiz ayarları sunucudan alınamadı:', error);
      }
    })();
  }

  await sharedSettingsHydrationPromise;
};

// Yarım kalan oturum yalnızca kendi egzersizinde geri yüklensin diye exerciseMode gönderilir.
// Uçuştaki istek önbelleği de moda göre anahtarlanmalı, yoksa ilk açılan egzersizin yanıtı
// diğerlerine servis edilir.
let groupingBootstrapMode: string | null = null;

export const fetchGroupingBootstrap = async (exerciseMode?: string) => {
  const mode = String(exerciseMode || '').trim();
  if (!groupingBootstrapInFlight || groupingBootstrapMode !== mode) {
    groupingBootstrapMode = mode;
    groupingBootstrapInFlight = Promise.all([
      ApiService.getApi('speed-reading/exercises/grouping/settings'),
      ApiService.getApi(`speed-reading/exercises/grouping/session/active${mode ? `?exerciseMode=${encodeURIComponent(mode)}` : ''}`),
    ]).finally(() => {
      groupingBootstrapInFlight = null;
    });
  }

  return groupingBootstrapInFlight;
};

export const getThemedLabelStyle = (): React.CSSProperties => ({
  color: '#295567',
});

export const getThemedBadgeStyle = (): React.CSSProperties => ({
  backgroundColor: THEME_PRIMARY_SOFT_CSS,
  color: THEME_PRIMARY_CSS,
});

export const getThemedOptionButtonStyle = (isActive: boolean): React.CSSProperties => ({
  borderColor: isActive ? THEME_PRIMARY_CSS : '#dde8ed',
  backgroundColor: isActive ? THEME_PRIMARY_SOFT_CSS : '#ffffff',
  color: isActive ? THEME_PRIMARY_CSS : '#446470',
});

export const getThemedSwatchStyle = (isActive: boolean, swatchHex: string): React.CSSProperties => ({
  borderColor: isActive ? '#ffffff' : '#d3dde3',
  boxShadow: isActive
    ? `0 0 0 2px ${swatchHex}, 0 4px 10px ${swatchHex}33`
    : '0 1px 3px rgba(20, 50, 70, 0.08)',
  transform: isActive ? 'scale(1.08)' : 'scale(1)',
});

export const getThemedRangeStyle = (value: number, min: number, max: number): React.CSSProperties => ({
  background: getSliderBackground(value, min, max, THEME_PRIMARY_CSS),
  accentColor: THEME_PRIMARY_CSS,
});

export const getThemedReviewSelectionStyle = (isActive: boolean): React.CSSProperties => (
  isActive
    ? {
      borderColor: 'color-mix(in srgb, var(--theme-primary) 30%, #d9e5ea)',
      backgroundColor: 'color-mix(in srgb, var(--theme-primary) 10%, white)',
    }
    : {}
);

export const getThemedReviewSelectionTextStyle = (isActive: boolean): React.CSSProperties => (
  isActive ? { color: THEME_PRIMARY_CSS } : {}
);

export const getThemedReviewSelectionDividerStyle = (isActive: boolean): React.CSSProperties => (
  isActive ? { backgroundColor: 'color-mix(in srgb, var(--theme-primary) 22%, #d4e2e8)' } : {}
);

export const getThemeChromeTextStyle = (): React.CSSProperties => ({
  color: THEME_PRIMARY_CSS,
});

export const getThemeChromeButtonStyle = (): React.CSSProperties => ({
  backgroundColor: THEME_PRIMARY_CSS,
});

export const PACE_MS_OPTIONS = [200, 300, 400, 500, 600, 800, 1000] as const;
export const PILOT_PACE_MS_OPTIONS = [20, 30, 40, 50, 75, 100, 125, 150, 200, 300, 400, 500, 600, 800, 1000, 1200, 1400, 1600, 1800, 2000, 2250, 2500] as const;
export const WORD_GROUP_OPTIONS = [1, 2, 3, 4, 5] as const;

export const paceMsToWordsPerMinute = (paceMs: number, wordsPerGroup = 1) => {
  const safeMs = Math.max(1, Number(paceMs) || 1);
  const safeWords = Math.max(1, Number(wordsPerGroup) || 1);
  return Math.round((safeWords * 60000) / safeMs);
};

export const wordsPerMinuteToClosestPaceMs = (wordsPerMinute: number, wordsPerGroup = 1) => {
  const safeWpm = Math.max(1, Number(wordsPerMinute) || 1);
  const safeWords = Math.max(1, Number(wordsPerGroup) || 1);
  const rawMs = Math.round((safeWords * 60000) / safeWpm);
  return PACE_MS_OPTIONS.reduce((closest, option) => (
    Math.abs(option - rawMs) < Math.abs(closest - rawMs) ? option : closest
  ), PACE_MS_OPTIONS[0]);
};

export const wordsPerMinuteToClosestPilotPaceMs = (wordsPerMinute: number) => {
  const safeWpm = Math.max(1, Number(wordsPerMinute) || 1);
  const rawMs = Math.round(60000 / safeWpm);
  return PILOT_PACE_MS_OPTIONS.reduce((closest, option) => (
    Math.abs(option - rawMs) < Math.abs(closest - rawMs) ? option : closest
  ), PILOT_PACE_MS_OPTIONS[0]);
};

export const PaceOptionSelector: React.FC<{
  wordsPerMinute: number;
  wordsPerGroup?: number;
  onSelect: (nextWordsPerMinute: number, nextPaceMs: number) => void;
}> = ({ wordsPerMinute, wordsPerGroup = 1, onSelect }) => {
  const selectedPaceMs = wordsPerMinuteToClosestPaceMs(wordsPerMinute, wordsPerGroup);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between text-sm font-semibold" style={getThemedLabelStyle()}>
        <span>Gösterim Hızı</span>
        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>{selectedPaceMs} ms</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {PACE_MS_OPTIONS.map((paceMs) => {
          const isActive = paceMs === selectedPaceMs;
          return (
            <button
              key={paceMs}
              onClick={() => onSelect(paceMsToWordsPerMinute(paceMs, wordsPerGroup), paceMs)}
              className="rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors"
              style={getThemedOptionButtonStyle(isActive)}
            >
              {paceMs} ms
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const WordGroupOptionSelector: React.FC<{
  value: number;
  onSelect: (nextWordsPerGroup: number) => void;
}> = ({ value, onSelect }) => (
  <div>
    <div className="mb-2 flex items-center justify-between text-sm font-semibold" style={getThemedLabelStyle()}>
      <span>Kelime Sayısı</span>
      <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>{value}</span>
    </div>
    <div className="flex flex-wrap gap-2">
      {WORD_GROUP_OPTIONS.map((option) => {
        const isActive = option === value;
        return (
          <button
            key={option}
            onClick={() => onSelect(option)}
            className="rounded-lg border px-4 py-2 text-sm font-semibold transition-colors"
            style={getThemedOptionButtonStyle(isActive)}
          >
            {option}
          </button>
        );
      })}
    </div>
  </div>
);

export const DifficultyOptionSelector: React.FC<{
  value: GroupingSettings['difficultyLevel'];
  onSelect: (nextDifficulty: GroupingSettings['difficultyLevel']) => void;
}> = ({ value, onSelect }) => (
  <div>
    <div className="mb-2 flex items-center justify-between text-sm font-semibold" style={getThemedLabelStyle()}>
      <span>Metin Zorluğu</span>
      <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>{value}</span>
    </div>
    <div className="grid grid-cols-2 gap-2">
      {DIFFICULTY_OPTIONS.map((option) => {
        const isActive = option === value;
        return (
          <button
            key={option}
            onClick={() => onSelect(option)}
            className="rounded-lg border px-3 py-2 text-sm font-semibold transition-colors"
            style={getThemedOptionButtonStyle(isActive)}
          >
            {option}
          </button>
        );
      })}
    </div>
  </div>
);

export const getGroupingTimingMetrics = (
  totalWordCount: number,
  wordsPerGroup: number,
  wordsPerMinute: number,
  difficultyLevel: GroupingSettings['difficultyLevel'],
  lastGroupText = ''
) => {
  const safeWpm = Math.max(1, wordsPerMinute || 1);
  const safeWordsPerGroup = Math.max(1, wordsPerGroup || 1);
  const difficultyMultiplier = GROUPING_DIFFICULTY_MULTIPLIER[difficultyLevel] || 1;
  const groupCount = Math.max(1, Math.ceil(Math.max(0, totalWordCount) / safeWordsPerGroup));
  const millisecondsPerGroup = Math.max(
    250,
    Math.round(((safeWordsPerGroup * 60 * 1000) / safeWpm) * difficultyMultiplier)
  );
  const totalRevealDurationMs = Math.max(0, (groupCount - 1) * millisecondsPerGroup);
  const lastGroupWordCount = Math.max(1, lastGroupText.trim().split(/\s+/).filter(Boolean).length || safeWordsPerGroup);
  const lastGroupCharacterCount = lastGroupText.replace(/\s+/g, '').length;
  const speedFactor = clamp(220 / safeWpm, 0.75, 1.2);
  const completionBufferMs = Math.round(
    clamp((320 + Math.min(220, lastGroupCharacterCount * 9) + Math.min(120, lastGroupWordCount * 45)) * speedFactor, 300, 700)
  );
  const totalDurationMs = totalRevealDurationMs + completionBufferMs;

  return {
    millisecondsPerGroup,
    totalRevealDurationMs,
    completionBufferMs,
    totalDurationMs,
    estimatedDurationSeconds: Math.max(0, Math.ceil(totalDurationMs / 1000)),
  };
};

export const getSliderBackground = (
  value: number,
  min: number,
  max: number,
  fillColor = THEME_PRIMARY_CSS,
  trackColor = '#d5dfe5'
) => {
  const safeMax = Math.max(min + 1, max);
  const progress = ((value - min) / (safeMax - min)) * 100;
  return `linear-gradient(90deg, ${fillColor} 0%, ${fillColor} ${progress}%, ${trackColor} ${progress}%, ${trackColor} 100%)`;
};

export const normalizePilotAnswer = (value: string) => value
  .trim()
  .toLocaleLowerCase('tr-TR')
  .replace(/[ç]/g, 'c')
  .replace(/[ğ]/g, 'g')
  .replace(/[ıi]/g, 'i')
  .replace(/[ö]/g, 'o')
  .replace(/[ş]/g, 's')
  .replace(/[ü]/g, 'u')
  .replace(/[^a-z0-9]/g, '');

export const getPilotRevealDuration = (wordsPerMinute: number, level: number) => {
  const baseDuration = 60000 / Math.max(1, wordsPerMinute);
  const levelMultiplier = 1 - ((clamp(level, 1, 13) - 1) * 0.025);
  return Math.round(clamp(baseDuration * levelMultiplier, 20, 2500));
};

export const getPilotCooldownDuration = (wordsPerMinute: number, level: number) => {
  const revealDuration = getPilotRevealDuration(wordsPerMinute, level);
  return Math.round(clamp(revealDuration * 1.15, 180, 900));
};

export const STOP_LOSS_GAP = 10;

export const shouldStopForWrongGap = (correctCount: number, wrongCount: number) => (
  wrongCount >= correctCount + STOP_LOSS_GAP
);

export const getMatchingRevealDuration = (wordsPerMinute: number, level: number) => {
  const millisecondsPerWord = 60000 / Math.max(1, wordsPerMinute);
  const levelAdjustment = 1 + ((clamp(level, 1, 13) - 1) * 0.03);
  return Math.round(clamp(millisecondsPerWord * 1.25 * levelAdjustment, 260, 1800));
};

export const getCaptureDurationSeconds = (wordsPerMinute: number, level: number, textWordCount: number) => {
  const safeWordCount = Math.max(1, Number(textWordCount) || 1);
  const safeWpm = Math.max(100, Number(wordsPerMinute) || 100);
  const baseReadSeconds = (safeWordCount / safeWpm) * 60;
  const levelFactor = 1 - ((clamp(level, 1, 13) - 1) * 0.015);
  const withBuffer = (baseReadSeconds * levelFactor) + 2;
  return Math.round(clamp(withBuffer, 8, 240));
};

export const getPilotWordPool = (level: number) => PILOT_TEST_LEVEL_WORDS[clamp(level, 1, 13)] || PILOT_TEST_LEVEL_WORDS[1];
export const getMultifocusWordPool = (level: number) => MULTIFOCUS_LEVEL_WORDS[clamp(level, 1, 13)] || MULTIFOCUS_LEVEL_WORDS[1];
export const getAntonymPairPool = (level: number) => ANTONYM_LEVEL_PAIRS[clamp(level, 1, 13)] || ANTONYM_LEVEL_PAIRS[1];
export const getSynonymPairPool = (level: number) => SYNONYM_LEVEL_PAIRS[clamp(level, 1, 13)] || SYNONYM_LEVEL_PAIRS[1];
export const getConcreteMeaningPool = (level: number) => CONCRETE_MEANING_LEVEL_WORDS[clamp(level, 1, 13)] || CONCRETE_MEANING_LEVEL_WORDS[1];

export const getAdaptiveFlashWordStyle = (
  fontSize: GroupingSettings['fontSize'],
  word: string,
  variant: 'center' | 'periphery'
): React.CSSProperties => {
  const length = Math.max(1, word.replace(/\s+/g, '').length);
  const base = PILOT_WORD_BASE_SIZE[fontSize] + (variant === 'center' ? 0 : -4);
  const min = PILOT_WORD_MIN_SIZE[fontSize];
  const reduction = Math.max(0, length - 5) * (variant === 'center' ? 2.4 : 2.8);
  const computedSize = clamp(base - reduction, min, base);

  return {
    fontSize: `${computedSize}px`,
    lineHeight: 1.15,
    whiteSpace: 'nowrap',
    letterSpacing: length > 10 ? '0.02em' : length > 7 ? '0.04em' : '0.06em',
    display: 'inline-block',
    verticalAlign: 'middle',
  };
};

export const getRandomPeripheryPosition = (): PeripheryPosition => {
  const quadrant = Math.floor(Math.random() * 4);
  const horizontalInset = `${8 + Math.round(Math.random() * 10)}%`;
  const verticalInset = `${10 + Math.round(Math.random() * 12)}%`;
  const width = '36%';

  switch (quadrant) {
    case 0:
      return { top: verticalInset, left: horizontalInset, width, textAlign: 'left' };
    case 1:
      return { top: verticalInset, right: horizontalInset, width, textAlign: 'right' };
    case 2:
      return { bottom: verticalInset, left: horizontalInset, width, textAlign: 'left' };
    default:
      return { bottom: verticalInset, right: horizontalInset, width, textAlign: 'right' };
  }
};

export const normalizeMatchingWordKey = (value: string) => String(value || '')
  .toLocaleLowerCase('tr-TR')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9ğüşöçıİ]/gi, '')
  .trim();

export const buildMatchingRoundKey = (left: string, right: string, modeName?: string) => {
  const leftKey = normalizeMatchingWordKey(left);
  const rightKey = normalizeMatchingWordKey(right);
  const sorted = [leftKey, rightKey].sort((a, b) => a.localeCompare(b, 'tr'));
  return `${modeName || 'matching'}:${sorted[0]}|${sorted[1]}`;
};

export const levenshteinDistance = (left: string, right: string) => {
  const a = normalizeMatchingWordKey(left);
  const b = normalizeMatchingWordKey(right);
  if (!a) return b.length;
  if (!b) return a.length;

  const rows = a.length + 1;
  const cols = b.length + 1;
  const matrix: number[][] = Array.from({ length: rows }, () => Array(cols).fill(0));

  for (let row = 0; row < rows; row += 1) matrix[row][0] = row;
  for (let col = 0; col < cols; col += 1) matrix[0][col] = col;

  for (let row = 1; row < rows; row += 1) {
    for (let col = 1; col < cols; col += 1) {
      const cost = a[row - 1] === b[col - 1] ? 0 : 1;
      matrix[row][col] = Math.min(
        matrix[row - 1][col] + 1,
        matrix[row][col - 1] + 1,
        matrix[row - 1][col - 1] + cost
      );
    }
  }

  return matrix[a.length][b.length];
};

export const isTooSimilarDistractor = (left: string, right: string) => {
  const a = normalizeMatchingWordKey(left);
  const b = normalizeMatchingWordKey(right);
  if (!a || !b) return false;
  if (a === b) return true;
  if (a.startsWith(b) || b.startsWith(a)) return true;

  const maxLength = Math.max(a.length, b.length);
  const distance = levenshteinDistance(a, b);
  const distanceRatio = maxLength > 0 ? distance / maxLength : 1;

  if (maxLength <= 5) return distanceRatio <= 0.4;
  if (maxLength <= 8) return distanceRatio <= 0.32;
  return distanceRatio <= 0.26;
};

export const buildMatchingRelationLookup = (pairs: WordPair[] = []) => {
  const lookup = new Map<string, Set<string>>();
  const addRelation = (left: string, right: string) => {
    const leftKey = normalizeMatchingWordKey(left);
    const rightKey = normalizeMatchingWordKey(right);
    if (!leftKey || !rightKey || leftKey === rightKey) return;
    if (!lookup.has(leftKey)) lookup.set(leftKey, new Set<string>());
    lookup.get(leftKey)?.add(rightKey);
  };

  pairs.forEach(([left, right]) => {
    addRelation(left, right);
    addRelation(right, left);
  });

  return lookup;
};

export const getMatchingPair = (
  level: number,
  modeName?: string,
  dynamicPairs?: WordPair[],
  options: MatchingPairGeneratorOptions = {}
) => {
  const normalizedModeKey = getModeCanonicalKey(modeName);
  const usedPairKeys = options.usedPairKeys || new Set<string>();
  const usedWordCounts = options.usedWordCounts || {};
  const forcedMatch = typeof options.forcedMatch === 'boolean' ? options.forcedMatch : null;
  const getUsage = (word: string) => usedWordCounts[normalizeMatchingWordKey(word)] || 0;
  const scoreCandidate = (candidate: { left: string; right: string; isMatch: boolean }) => {
    const pairKey = buildMatchingRoundKey(candidate.left, candidate.right, modeName);
    const repeatedPairPenalty = usedPairKeys.has(pairKey) ? 8 : 0;
    const usagePenalty = getUsage(candidate.left) + getUsage(candidate.right);
    const similarityPenalty =
      (normalizedModeKey === 'es-anlam' || normalizedModeKey === 'zit-anlam')
        && !candidate.isMatch
        && isTooSimilarDistractor(candidate.left, candidate.right)
        ? 7
        : 0;
    return repeatedPairPenalty + usagePenalty + similarityPenalty;
  };

  const pickBaseCandidate = () => {
    if (normalizedModeKey === 'zit-anlam') {
      const pairs = Array.isArray(dynamicPairs) ? dynamicPairs : [];
      if (pairs.length === 0) return null;
      const shouldMatch = forcedMatch !== null ? forcedMatch : Math.random() > 0.5;
      const relationLookup = buildMatchingRelationLookup(pairs);

      if (shouldMatch) {
        const [leftWord, rightWord] = pairs[Math.floor(Math.random() * pairs.length)] || ['', ''];
        const shouldReverse = Math.random() > 0.5;
        return {
          left: shouldReverse ? rightWord : leftWord,
          right: shouldReverse ? leftWord : rightWord,
          isMatch: true,
        };
      }

      const words = [...new Set(pairs.flat())];
      let left = words[Math.floor(Math.random() * words.length)] || 'iyi';
      let right = words[Math.floor(Math.random() * words.length)] || 'güzel';
      let guard = 0;

      while (guard < 80) {
        const leftKey = normalizeMatchingWordKey(left);
        const rightKey = normalizeMatchingWordKey(right);
        const isKnownOpposite = relationLookup.get(leftKey)?.has(rightKey) || false;
        const isInvalid = leftKey === rightKey || isKnownOpposite || isTooSimilarDistractor(left, right);
        if (!isInvalid) break;
        left = words[Math.floor(Math.random() * words.length)] || 'iyi';
        right = words[Math.floor(Math.random() * words.length)] || 'güzel';
        guard += 1;
      }

      return {
        left,
        right,
        isMatch: false,
      };
    }

    if (normalizedModeKey === 'es-anlam') {
      const pairs = Array.isArray(dynamicPairs) ? dynamicPairs : [];
      if (pairs.length === 0) return null;
      const shouldMatch = forcedMatch !== null ? forcedMatch : Math.random() > 0.5;
      const relationLookup = buildMatchingRelationLookup(pairs);

      if (shouldMatch) {
        const [leftWord, rightWord] = pairs[Math.floor(Math.random() * pairs.length)] || ['', ''];
        const shouldReverse = Math.random() > 0.5;
        return {
          left: shouldReverse ? rightWord : leftWord,
          right: shouldReverse ? leftWord : rightWord,
          isMatch: true,
        };
      }

      const words = [...new Set(pairs.flat())];
      let left = words[Math.floor(Math.random() * words.length)] || 'görev';
      let right = words[Math.floor(Math.random() * words.length)] || 'kalem';
      let guard = 0;

      while (guard < 80) {
        const leftKey = normalizeMatchingWordKey(left);
        const rightKey = normalizeMatchingWordKey(right);
        const isKnownSynonym = relationLookup.get(leftKey)?.has(rightKey) || false;
        const isInvalid = leftKey === rightKey || isKnownSynonym || isTooSimilarDistractor(left, right);
        if (!isInvalid) break;
        left = words[Math.floor(Math.random() * words.length)] || 'görev';
        right = words[Math.floor(Math.random() * words.length)] || 'kalem';
        guard += 1;
      }

      return {
        left,
        right,
        isMatch: false,
      };
    }

    const pool = Array.isArray(options.dynamicWords) ? options.dynamicWords : [];
    if (pool.length === 0) return null;
    const left = pool[Math.floor(Math.random() * pool.length)] || pool[0];
    const shouldMatch = forcedMatch !== null ? forcedMatch : Math.random() > 0.5;

    if (shouldMatch) {
      return {
        left,
        right: left,
        isMatch: true,
      };
    }

    const alternatives = pool.filter((word) => word !== left);
    const right = alternatives[Math.floor(Math.random() * alternatives.length)] || `${left}x`;

    return {
      left,
      right,
      isMatch: false,
    };
  };

  let bestCandidate = pickBaseCandidate();
  if (!bestCandidate) return null;
  let bestScore = scoreCandidate(bestCandidate);

  for (let attempt = 0; attempt < 80; attempt += 1) {
    const candidate = pickBaseCandidate();
    if (!candidate) continue;
    const candidateScore = scoreCandidate(candidate);
    if (candidateScore < bestScore) {
      bestCandidate = candidate;
      bestScore = candidateScore;
    }

    if (candidateScore <= 1) {
      return candidate;
    }
  }

  return bestCandidate;
};

export const ModeIllustration: React.FC<{ mode: ModeConfig }> = ({ mode }) => {
  const soft = `${mode.accent}30`;
  const a = mode.accent;

  switch (mode.illustration) {
    case 'group':
      return (
        <div className="flex flex-col items-center gap-2">
          <div className="flex flex-col items-center space-y-[5px]">
            {LINES.map((w, i) => <MiniLine key={i} width={w - 10} color={a} />)}
          </div>
          <div className="flex items-center gap-[4px] mt-1">
            <span className="h-[13px] w-[22px] rounded-full" style={{ backgroundColor: a }} />
            <span className="h-[13px] w-[26px] rounded-full" style={{ backgroundColor: a }} />
            <span className="h-[13px] w-[34px] rounded-full" style={{ backgroundColor: a }} />
          </div>
        </div>
      );

    case 'shadow':
      return (
        <div className="flex flex-col items-center gap-2">
          <div className="flex flex-col items-center space-y-[5px]">
            {LINES.map((w, i) => <MiniLine key={i} width={w - 6} color={a} />)}
          </div>
          <span
            className="block h-[12px] w-[44px] rounded-full border-[2.5px] mt-1"
            style={{ borderColor: a, backgroundColor: soft }}
          />
        </div>
      );

    case 'block':
      return (
        <div className="relative flex h-[92px] w-[102px] items-center justify-center">
          <span className="absolute left-0 top-0 h-[24px] w-[26px] rounded-tl-[16px] border-l-[3px] border-t-[3px] opacity-30" style={{ borderColor: a }} />
          <span className="absolute right-0 top-0 h-[24px] w-[26px] rounded-tr-[16px] border-r-[3px] border-t-[3px] opacity-30" style={{ borderColor: a }} />
          <span className="absolute bottom-0 left-0 h-[24px] w-[26px] rounded-bl-[16px] border-b-[3px] border-l-[3px] opacity-30" style={{ borderColor: a }} />
          <span className="absolute bottom-0 right-0 h-[24px] w-[26px] rounded-br-[16px] border-b-[3px] border-r-[3px] opacity-30" style={{ borderColor: a }} />
          <div className="flex items-center gap-2">
            <span
              className="flex h-[28px] w-[52px] items-center justify-center rounded-full border-[2.5px]"
              style={{ borderColor: a, backgroundColor: soft }}
            >
              <span className="block h-[4px] w-[24px] rounded-full opacity-40" style={{ backgroundColor: a }} />
            </span>
            <ChevronRight className="h-6 w-6 opacity-30" style={{ color: a }} />
          </div>
        </div>
      );

    case 'blockReading':
      return (
        <div className="relative flex h-[100px] w-[116px] items-center justify-center">
          <div
            className="relative flex h-[46px] w-[104px] items-center justify-center rounded-[7px] shadow-[0_12px_26px_rgba(21,82,99,0.14)]"
            style={{ backgroundColor: a }}
          >
            <span className="absolute -top-[12px] left-1/2 h-[8px] w-[2px] -translate-x-1/2 rounded-full opacity-45" style={{ backgroundColor: a }} />
            <span className="absolute -bottom-[12px] left-1/2 h-[8px] w-[2px] -translate-x-1/2 rounded-full opacity-45" style={{ backgroundColor: a }} />
            <span className="absolute left-1/2 top-[8px] h-[30px] w-[1.5px] -translate-x-1/2 rounded-full bg-white/28" />
            <div className="flex items-center gap-[6px]">
              <span className="text-[19px] font-black leading-none text-white/90">Aa</span>
              <span className="h-[4px] w-[10px] rounded-full bg-white/35" />
              <span className="text-[19px] font-black leading-none text-white/90">Aa</span>
            </div>
          </div>
        </div>
      );

    case 'text':
      return (
        <div className="flex flex-col items-center gap-2">
          <div className="flex h-[38px] w-[38px] items-center justify-center rounded-full border-[2.5px]" style={{ borderColor: a }}>
            <CircleHelp className="h-5 w-5" style={{ color: a }} />
          </div>
          <div className="flex flex-col items-center space-y-[5px]">
            {LINES.slice(0, 4).map((w, i) => <MiniLine key={i} width={w} color={a} />)}
          </div>
        </div>
      );

    case 'incompleteText':
      return (
        <div className="flex h-[100px] w-[118px] items-center justify-center">
          <div
            className="relative h-[76px] w-[104px] rounded-[18px] border-[2.5px] bg-white/70 px-[12px] py-[12px]"
            style={{ borderColor: `${a}55`, boxShadow: `0 14px 30px ${a}16` }}
          >
            <span
              className="absolute -left-[7px] top-[20px] h-[16px] w-[16px] rounded-[6px] border-[2.5px] bg-white"
              style={{ borderColor: a }}
            />
            <span
              className="absolute -right-[6px] bottom-[16px] h-[12px] w-[12px] rounded-full"
              style={{ backgroundColor: a, opacity: 0.42 }}
            />
            <div className="flex flex-col gap-[8px]">
              <div className="flex items-center gap-[4px]">
                <span className="block h-[7px] w-[18px] rounded-full" style={{ backgroundColor: a }} />
                <span className="block h-[7px] w-[7px] rounded-full opacity-34" style={{ backgroundColor: a }} />
                <span className="block h-[7px] w-[24px] rounded-full" style={{ backgroundColor: a, opacity: 0.52 }} />
                <span className="block h-[7px] w-[7px] rounded-full opacity-34" style={{ backgroundColor: a }} />
              </div>
              <div className="flex items-center gap-[4px]">
                <span className="block h-[7px] w-[30px] rounded-full opacity-42" style={{ backgroundColor: a }} />
                <span className="block h-[7px] w-[7px] rounded-full" style={{ backgroundColor: a }} />
                <span className="block h-[7px] w-[7px] rounded-full" style={{ backgroundColor: a }} />
                <span className="block h-[7px] w-[20px] rounded-full opacity-42" style={{ backgroundColor: a }} />
              </div>
              <div className="flex items-center gap-[4px]">
                <span className="block h-[7px] w-[14px] rounded-full opacity-45" style={{ backgroundColor: a }} />
                <span className="block h-[7px] w-[7px] rounded-full" style={{ backgroundColor: a }} />
                <span className="block h-[7px] w-[26px] rounded-full opacity-52" style={{ backgroundColor: a }} />
                <span className="block h-[7px] w-[7px] rounded-full" style={{ backgroundColor: a }} />
              </div>
            </div>
          </div>
        </div>
      );

    case 'pilot':
      return (
        <div className="relative flex h-[100px] w-[100px] items-center justify-center">
          <span className="absolute h-[72px] w-[72px] rounded-full border-[3px] opacity-15" style={{ borderColor: a }} />
          <span className="absolute h-[54px] w-[54px] rounded-full border-[3px] opacity-28" style={{ borderColor: a }} />
          <span className="absolute left-1/2 top-0 h-[18px] w-[3px] -translate-x-1/2 rounded-full opacity-20" style={{ backgroundColor: a }} />
          <span className="absolute bottom-0 left-1/2 h-[18px] w-[3px] -translate-x-1/2 rounded-full opacity-20" style={{ backgroundColor: a }} />
          <span className="absolute left-0 top-1/2 h-[3px] w-[18px] -translate-y-1/2 rounded-full opacity-20" style={{ backgroundColor: a }} />
          <span className="absolute right-0 top-1/2 h-[3px] w-[18px] -translate-y-1/2 rounded-full opacity-20" style={{ backgroundColor: a }} />
          <span
            className="relative z-10 flex h-[24px] w-[44px] items-center justify-center rounded-full border-[2.5px]"
            style={{ borderColor: a, backgroundColor: soft }}
          >
            <span className="block h-[4px] w-[18px] rounded-full opacity-50" style={{ backgroundColor: a }} />
          </span>
        </div>
      );

    case 'wordGlance':
      return (
        <div className="flex h-[100px] w-[118px] items-center justify-center">
          <div className="flex flex-col items-center gap-[7px]">
            <div className="flex items-center justify-center gap-[4px]">
              <span className="block h-[8px] w-[15px] rounded-full" style={{ backgroundColor: '#ff0000' }} />
              <span className="block h-[8px] w-[22px] rounded-full opacity-55" style={{ backgroundColor: '#2f3f49' }} />
              <span className="block h-[8px] w-[36px] rounded-full opacity-45" style={{ backgroundColor: '#2f3f49' }} />
              <span className="block h-[8px] w-[16px] rounded-full" style={{ backgroundColor: '#ff0000' }} />
            </div>
            <div className="flex items-center justify-center gap-[4px] opacity-55">
              <span className="block h-[5px] w-[30px] rounded-full" style={{ backgroundColor: a }} />
              <span className="block h-[5px] w-[42px] rounded-full" style={{ backgroundColor: a }} />
              <span className="block h-[5px] w-[24px] rounded-full" style={{ backgroundColor: a }} />
            </div>
            <div className="flex items-center justify-center gap-[4px] opacity-35">
              <span className="block h-[5px] w-[22px] rounded-full" style={{ backgroundColor: a }} />
              <span className="block h-[5px] w-[54px] rounded-full" style={{ backgroundColor: a }} />
            </div>
          </div>
        </div>
      );

    case 'jumpGlance': {
      const jumpMarks = [
        { key: 'top-left', className: 'left-[-2px] top-[2px] opacity-70 scale-[0.82]', align: 'left' },
        { key: 'top-right', className: 'right-[-2px] top-[2px] opacity-70 scale-[0.82]', align: 'right' },
        { key: 'center', className: 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 scale-100', align: 'center' },
        { key: 'bottom-left', className: 'bottom-[2px] left-[-2px] opacity-70 scale-[0.82]', align: 'left' },
        { key: 'bottom-right', className: 'bottom-[2px] right-[-2px] opacity-70 scale-[0.82]', align: 'right' },
      ];

      return (
        <div className="relative flex h-[100px] w-[118px] items-center justify-center">
          {jumpMarks.map((mark) => (
            <div
              key={mark.key}
              className={`absolute origin-center ${mark.className}`}
              style={{ width: mark.key === 'center' ? 72 : 58 }}
            >
              <div
                className="flex items-center gap-[3px]"
                style={{
                  justifyContent: mark.align === 'right' ? 'flex-end' : mark.align === 'center' ? 'center' : 'flex-start',
                }}
              >
                <span className="block h-[7px] w-[12px] rounded-full" style={{ backgroundColor: '#ff0000' }} />
                <span className="block h-[7px] w-[20px] rounded-full opacity-55" style={{ backgroundColor: '#2f3f49' }} />
                <span className="block h-[7px] w-[12px] rounded-full" style={{ backgroundColor: '#ff0000' }} />
              </div>
            </div>
          ))}
        </div>
      );
    }

    case 'textSearch':
      return (
        <div className="flex h-[100px] w-[118px] items-center justify-center">
          <div className="flex flex-col gap-[6px]">
            <span className="block h-[5px] w-[102px] rounded-full opacity-26" style={{ backgroundColor: a }} />
            <span className="block h-[5px] w-[96px] rounded-full opacity-22" style={{ backgroundColor: a }} />
            <div className="flex items-center gap-[5px]">
              <span className="block h-[5px] w-[38px] rounded-full opacity-22" style={{ backgroundColor: a }} />
              <span
                className="block h-[18px] w-[36px] rounded-[6px]"
                style={{ backgroundColor: a, boxShadow: `0 0 0 1px ${a}22` }}
              />
              <span className="block h-[5px] w-[20px] rounded-full opacity-22" style={{ backgroundColor: a }} />
            </div>
            <span className="block h-[5px] w-[94px] rounded-full opacity-22" style={{ backgroundColor: a }} />
            <span className="block h-[5px] w-[88px] rounded-full opacity-18" style={{ backgroundColor: a }} />
            <span className="block h-[5px] w-[74px] rounded-full opacity-18" style={{ backgroundColor: a }} />
          </div>
        </div>
      );

    case 'periphery':
      return (
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-2">
            <span className="block w-[30px] h-[18px] rounded-md border-2" style={{ borderColor: a, backgroundColor: soft }} />
            <span className="block w-[10px] h-[10px] rounded-full" style={{ backgroundColor: a }} />
            <span className="block w-[30px] h-[18px] rounded-md border-2" style={{ borderColor: a, backgroundColor: soft }} />
          </div>
          <div className="flex flex-col items-center space-y-[5px]">
            {LINES.slice(0, 4).map((w, i) => <MiniLine key={i} width={w - 2} color={a} />)}
          </div>
        </div>
      );

    case 'match':
      return (
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-4">
            <ChevronLeft className="w-5 h-5" style={{ color: a }} />
            <ChevronRight className="w-5 h-5" style={{ color: a }} />
          </div>
          <div className="flex flex-col items-center space-y-[5px]">
            {LINES.slice(0, 4).map((w, i) => <MiniLine key={i} width={w - 8} color={a} />)}
          </div>
        </div>
      );

    case 'multifocus':
      return (
        <div className="flex flex-col items-center gap-2">
          <div className="grid grid-cols-2 gap-[5px]">
            <span className="block w-[8px] h-[8px] rounded-full" style={{ backgroundColor: a, opacity: 0.5 }} />
            <span className="block w-[8px] h-[8px] rounded-full" style={{ backgroundColor: a }} />
            <span className="block w-[8px] h-[8px] rounded-full" style={{ backgroundColor: a }} />
            <span className="block w-[8px] h-[8px] rounded-full" style={{ backgroundColor: a, opacity: 0.5 }} />
          </div>
          <div className="flex flex-col items-center space-y-[5px]">
            {LINES.slice(0, 4).map((w, i) => <MiniLine key={i} width={w - 6} color={a} />)}
          </div>
        </div>
      );

    case 'antonym':
      return (
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-2">
            <span className="block w-[26px] h-[12px] rounded-full" style={{ backgroundColor: a }} />
            <Minus className="w-3 h-3" style={{ color: a }} />
            <span className="block w-[26px] h-[12px] rounded-full" style={{ backgroundColor: a, opacity: 0.45 }} />
          </div>
          <div className="flex flex-col items-center space-y-[5px]">
            {LINES.slice(0, 4).map((w, i) => <MiniLine key={i} width={w - 4} color={a} />)}
          </div>
        </div>
      );

    case 'synonym':
      return (
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-2">
            <span className="block w-[26px] h-[12px] rounded-full border-2" style={{ borderColor: a, backgroundColor: soft }} />
            <span className="block w-[6px] h-[6px] rounded-full" style={{ backgroundColor: a }} />
            <span className="block w-[26px] h-[12px] rounded-full border-2" style={{ borderColor: a, backgroundColor: soft }} />
          </div>
          <div className="flex flex-col items-center space-y-[5px]">
            {LINES.slice(0, 4).map((w, i) => <MiniLine key={i} width={w - 4} color={a} />)}
          </div>
        </div>
      );

    case 'similarWords':
      return (
        <div className="flex h-[100px] w-[118px] items-center justify-center">
          <div className="grid grid-cols-2 gap-[7px]">
            {[
              { active: false, width: 38 },
              { active: false, width: 34 },
              { active: true, width: 42 },
              { active: false, width: 36 },
            ].map((tile, index) => (
              <div
                key={`similar-words-${index}`}
                className={`flex h-[30px] w-[46px] flex-col items-center justify-center rounded-[12px] border-[2px] ${tile.active ? '' : 'border-dashed'
                  }`}
                style={{
                  borderColor: tile.active ? a : `${a}88`,
                  backgroundColor: tile.active ? a : `${a}08`,
                }}
              >
                <span
                  className="block h-[4px] rounded-full"
                  style={{ width: `${tile.width}px`, backgroundColor: tile.active ? '#FFFFFF' : a, opacity: tile.active ? 0.92 : 0.62 }}
                />
                <span
                  className="mt-[4px] block h-[4px] rounded-full"
                  style={{ width: `${Math.max(tile.width - 6, 20)}px`, backgroundColor: tile.active ? '#FFFFFF' : a, opacity: tile.active ? 0.82 : 0.48 }}
                />
              </div>
            ))}
          </div>
        </div>
      );

    case 'concrete':
      return (
        <div className="flex flex-col items-center gap-2">
          <div className="flex w-[90px] items-start justify-between">
            <div className="flex flex-col items-center space-y-[4px]">
              {LINES.slice(0, 3).map((w, i) => <MiniLine key={i} width={w - 18} color={a} />)}
            </div>
            <div className="flex h-[32px] w-[32px] items-center justify-center rounded-full border-[2px]" style={{ borderColor: `${a}80` }}>
              <ScanSearch className="h-[14px] w-[14px]" style={{ color: a }} />
            </div>
          </div>
          <div className="flex flex-col items-center space-y-[5px]">
            {LINES.slice(0, 3).map((w, i) => <MiniLine key={i} width={w} color={a} />)}
          </div>
        </div>
      );

    case 'capture':
      return (
        <div className="flex flex-col items-center gap-2">
          <div className="flex h-[32px] w-[80px] items-center justify-around rounded-[10px]" style={{ backgroundColor: a }}>
            <ArrowDown className="h-[14px] w-[14px] text-white" />
            <ArrowDown className="h-[14px] w-[14px] text-white" />
          </div>
          <div className="flex flex-col items-center space-y-[5px]">
            {LINES.slice(0, 4).map((w, i) => <MiniLine key={i} width={w - 8} color={a} />)}
          </div>
        </div>
      );

    case 'disappearingText':
      return (
        <div className="relative flex h-[100px] w-[118px] items-center justify-center">
          <div
            className="relative h-[72px] w-[96px] overflow-hidden rounded-[14px] border px-[10px] py-[11px]"
            style={{ borderColor: `${a}24`, backgroundColor: '#FFFFFF' }}
          >
            <span
              className="absolute bottom-[10px] left-[10px] top-[10px] w-[30px] rounded-[10px]"
              style={{ background: `linear-gradient(90deg, ${soft} 0%, ${a}10 62%, transparent 100%)` }}
            />
            <div className="relative z-10 flex h-full flex-col justify-between">
              {[
                { left: 30, right: 50 },
                { left: 22, right: 58 },
                { left: 34, right: 42 },
                { left: 18, right: 64 },
                { left: 28, right: 48 },
              ].map((line, index) => (
                <div key={`disappearing-line-${index}`} className="flex items-center gap-[5px]">
                  <span
                    className="block h-[5px] rounded-full"
                    style={{ width: `${line.left}px`, backgroundColor: a, opacity: 0.14 }}
                  />
                  <span
                    className="block h-[5px] rounded-full"
                    style={{ width: `${line.right}px`, backgroundColor: a, opacity: 0.58 }}
                  />
                </div>
              ))}
            </div>
            <span
              className="absolute bottom-[8px] left-[40px] top-[8px] w-[2px] rounded-full"
              style={{ backgroundColor: a, opacity: 0.38 }}
            />
          </div>
        </div>
      );

    case 'wordSearch':
      return (
        <div className="flex h-[100px] w-[118px] items-center justify-center">
          <div className="grid grid-cols-5 gap-[4px] rounded-[18px] border p-[8px]" style={{ borderColor: `${a}22`, backgroundColor: `${a}06` }}>
            {Array.from({ length: 20 }, (_, index) => {
              const row = Math.floor(index / 5);
              const column = index % 5;
              const active = column === 3 && row > 0;

              return (
                <span
                  key={`word-search-${index}`}
                  className="block h-[14px] w-[14px] rounded-[5px] border"
                  style={{
                    borderColor: active ? a : `${a}24`,
                    backgroundColor: active ? a : '#FFFFFF',
                    boxShadow: active ? `0 1px 2px ${a}22` : 'none',
                  }}
                />
              );
            })}
          </div>
        </div>
      );

    case 'colorFind':
      return (
        <div className="flex h-[100px] w-[118px] items-center justify-center">
          <div className="grid grid-cols-3 gap-[7px]">
            {['#E53935', '#1E88E5', '#43A047', '#FDD835', '#8E24AA', '#FB8C00'].map((color, index) => (
              <span
                key={`color-find-${color}`}
                className="flex h-[24px] w-[24px] items-center justify-center rounded-[9px] text-[10px] font-black text-white shadow-sm"
                style={{ backgroundColor: color, opacity: index === 3 ? 0.72 : 1 }}
              >
                A
              </span>
            ))}
          </div>
        </div>
      );

    case 'letterCount':
      return (
        <div className="flex h-[100px] w-[118px] items-center justify-center">
          <div className="grid grid-cols-6 gap-[4px] rounded-[18px] border p-[8px]" style={{ borderColor: `${a}22`, backgroundColor: `${a}06` }}>
            {Array.from({ length: 30 }, (_, index) => {
              const active = [4, 11, 16, 24].includes(index);
              return (
                <span
                  key={`letter-count-${index}`}
                  className="flex h-[13px] w-[13px] items-center justify-center rounded-[4px] text-[9px] font-black"
                  style={{
                    color: active ? '#ffffff' : a,
                    backgroundColor: active ? a : '#ffffff',
                    border: `1px solid ${active ? a : `${a}24`}`,
                  }}
                >
                  {active ? 'n' : 'm'}
                </span>
              );
            })}
          </div>
        </div>
      );

    case 'numberCount':
      return (
        <div className="flex h-[100px] w-[118px] items-center justify-center">
          <div className="grid grid-cols-4 gap-[5px] rounded-[18px] border p-[8px]" style={{ borderColor: `${a}22`, backgroundColor: `${a}06` }}>
            {['12', '34', '78', '65', '34', '90', '27', '43', '56', '34', '81', '19'].map((number, index) => {
              const active = number === '34';
              return (
                <span
                  key={`number-count-${index}`}
                  className="flex h-[18px] w-[23px] items-center justify-center rounded-[6px] text-[9px] font-black"
                  style={{
                    color: active ? '#ffffff' : a,
                    backgroundColor: active ? a : '#ffffff',
                    border: `1px solid ${active ? a : `${a}24`}`,
                  }}
                >
                  {number}
                </span>
              );
            })}
          </div>
        </div>
      );

    case 'attentionPuzzle':
      return (
        <div className="flex h-[100px] w-[118px] items-center justify-center">
          <div className="grid grid-cols-3 gap-[6px] rounded-[18px] border p-[9px]" style={{ borderColor: `${a}22`, backgroundColor: `${a}06` }}>
            {Array.from({ length: 9 }, (_, index) => (
              <span
                key={`attention-puzzle-${index}`}
                className="flex h-[19px] w-[19px] items-center justify-center rounded-[7px] text-[13px] font-black"
                style={{
                  color: index === 4 ? '#8aa0aa' : a,
                  backgroundColor: index === 4 ? '#ffffff' : `${a}16`,
                  border: `1px ${index === 4 ? 'dashed' : 'solid'} ${index === 4 ? '#b7c8d0' : `${a}44`}`,
                }}
              >
                {index === 4 ? '?' : index % 2 === 0 ? '●' : '◆'}
              </span>
            ))}
          </div>
        </div>
      );

    case 'spotDifference':
      return (
        <div className="flex h-[100px] w-[118px] items-center justify-center gap-[8px]">
          {[0, 1].map((panel) => (
            <div key={`spot-panel-${panel}`} className="grid grid-cols-3 gap-[4px] rounded-[13px] border p-[6px]" style={{ borderColor: `${a}22`, backgroundColor: `${a}06` }}>
              {Array.from({ length: 9 }, (_, index) => {
                const changed = panel === 1 && [2, 6].includes(index);
                return (
                  <span
                    key={`spot-difference-${panel}-${index}`}
                    className="h-[12px] w-[12px] rounded-[4px] border"
                    style={{
                      borderColor: changed ? a : `${a}24`,
                      backgroundColor: changed ? a : '#ffffff',
                    }}
                  />
                );
              })}
            </div>
          ))}
        </div>
      );

    case 'magic':
      return (
        <div className="flex flex-col items-center gap-2">
          <span
            className="block h-[10px] rounded-full border-2"
            style={{ width: '90px', borderColor: a, backgroundColor: soft }}
          />
          <div className="flex flex-col items-center space-y-[5px]">
            {LINES.slice(0, 4).map((w, i) => <MiniLine key={i} width={w - 4} color={a} />)}
          </div>
        </div>
      );

    case 'focused':
      return (
        <div className="flex flex-col items-center gap-2">
          <div className="flex flex-col items-center space-y-[3px]">
            {[70, 80, 70, 80, 70].map((w, i) => (
              <span
                key={i}
                className="block h-[5px] rounded-sm"
                style={{ width: `${w}px`, backgroundColor: a, opacity: i === 2 ? 0.7 : 0.3 }}
              />
            ))}
          </div>
        </div>
      );

    case 'dailyHomework':
      return (
        <div className="flex h-[100px] w-[118px] items-center justify-center">
          <div
            className="relative h-[78px] w-[92px] rounded-[18px] border-[2.5px] bg-white/75 px-[12px] pb-[10px] pt-[17px]"
            style={{ borderColor: `${a}55`, boxShadow: `0 14px 30px ${a}16` }}
          >
            <div className="absolute left-[15px] right-[15px] top-[9px] flex items-center justify-between">
              <span className="h-[8px] w-[8px] rounded-full" style={{ backgroundColor: a, opacity: 0.72 }} />
              <span className="h-[8px] w-[8px] rounded-full" style={{ backgroundColor: a, opacity: 0.72 }} />
            </div>
            <span className="absolute left-[12px] right-[12px] top-[25px] h-[2px] rounded-full opacity-25" style={{ backgroundColor: a }} />
            <div className="mt-[17px] flex flex-col gap-[8px]">
              {[0, 1].map((row) => (
                <div key={`daily-homework-${row}`} className="flex items-center gap-[7px]">
                  <span
                    className="flex h-[14px] w-[14px] items-center justify-center rounded-[5px] border-[2px] text-[10px] font-black leading-none"
                    style={{ borderColor: a, color: '#ffffff', backgroundColor: a }}
                  >
                    ✓
                  </span>
                  <span
                    className="block h-[6px] rounded-full"
                    style={{ width: row === 1 ? 45 : 54, backgroundColor: a, opacity: 0.58 }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      );

    case 'metronome':
      return (
        <div className="relative flex h-[100px] w-[100px] items-center justify-center">
          <span className="absolute h-px w-[84px] rounded-full opacity-30" style={{ backgroundColor: a }} />
          <span className="absolute left-[18px] h-[16px] w-[16px] rounded-full shadow-[0_0_14px_rgba(88,183,204,0.45)]" style={{ backgroundColor: a }} />
          <span className="absolute right-[18px] h-[16px] w-[16px] rounded-full opacity-45" style={{ backgroundColor: a }} />
          <span className="absolute left-1/2 top-1/2 h-[8px] w-[8px] -translate-x-1/2 -translate-y-1/2 rounded-full border" style={{ borderColor: `${a}88`, backgroundColor: soft }} />
        </div>
      );

    case 'squares':
      return (
        <div className="relative flex h-[100px] w-[100px] items-center justify-center">
          <svg viewBox="0 0 100 100" className="h-[84px] w-[84px]">
            {[56, 72, 88].map((size, index) => {
              const offset = (100 - size) / 2;
              return (
                <rect
                  key={size}
                  x={offset}
                  y={offset}
                  width={size}
                  height={size}
                  fill="none"
                  stroke={a}
                  strokeWidth="2.5"
                  opacity={0.18 + index * 0.12}
                />
              );
            })}
            <circle cx="50" cy="50" r="5.5" fill={a} />
          </svg>
        </div>
      );

    case 'nodes13':
      return (
        <div className="relative flex h-[100px] w-[100px] items-center justify-center">
          <svg viewBox="0 0 100 100" className="h-[84px] w-[84px]">
            {[
              ['M50 50 L50 12'],
              ['M50 12 L19 76'],
              ['M19 76 L81 76'],
              ['M81 76 L19 32'],
              ['M19 32 L50 90'],
              ['M50 90 L81 32'],
              ['M81 32 L13 50'],
              ['M13 50 L69 82'],
              ['M69 82 L69 26'],
              ['M69 26 L31 82'],
              ['M31 82 L31 26'],
              ['M31 26 L87 50'],
            ].map(([d], index) => (
              <path
                key={`segment-${index}`}
                d={d}
                fill="none"
                stroke={soft}
                strokeWidth="2.2"
                opacity={0.34}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ))}

            {[
              { x: 50, y: 12, r: 6.1, opacity: 0.9 },
              { x: 19, y: 76, r: 5.4, opacity: 0.78 },
              { x: 81, y: 76, r: 5.4, opacity: 0.78 },
              { x: 19, y: 32, r: 5.4, opacity: 0.78 },
              { x: 50, y: 90, r: 5.4, opacity: 0.76 },
              { x: 81, y: 32, r: 5.4, opacity: 0.78 },
              { x: 13, y: 50, r: 5.4, opacity: 0.74 },
              { x: 69, y: 82, r: 4.8, opacity: 0.72 },
              { x: 69, y: 26, r: 4.8, opacity: 0.72 },
              { x: 31, y: 82, r: 4.8, opacity: 0.72 },
              { x: 31, y: 26, r: 4.8, opacity: 0.72 },
              { x: 87, y: 50, r: 5.4, opacity: 0.74 },
            ].map((node, index) => (
              <circle key={`node-${index}`} cx={node.x} cy={node.y} r={node.r} fill={soft} opacity={node.opacity} />
            ))}

            <circle cx="50" cy="50" r="8" fill={a} />
            <circle cx="50" cy="12" r="6.1" fill={soft} opacity="0.9" />
          </svg>
        </div>
      );

    case 'ponte':
      return (
        <div className="relative flex h-[100px] w-[100px] items-center justify-center">
          <svg viewBox="0 0 100 100" className="h-[82px] w-[82px]">
            <path
              d="M56 7
                 C 25 7, 19 25, 52.5 32.5
                 C 70.5 36.5, 68.5 54.5, 27.5 61
                 C 11 63.5, 13 85.5, 59 92.5"
              fill="none"
              stroke={a}
              strokeWidth="4"
              opacity="0.58"
              strokeLinecap="round"
            />
            <circle cx="40" cy="18.5" r="6.2" fill={soft} opacity="0.92" />
            <circle cx="40" cy="44.5" r="6.2" fill={soft} opacity="0.82" />
            <circle cx="40" cy="70" r="6.2" fill={soft} opacity="0.72" />
            <circle cx="40" cy="18.5" r="2.9" fill={a} opacity="0.62" />
            <circle cx="40" cy="44.5" r="2.9" fill={a} opacity="0.22" />
            <circle cx="40" cy="70" r="2.9" fill={a} opacity="0.18" />
            <circle cx="56" cy="7" r="8" fill={a} />
            <rect x="66.5" y="4.9" width="8.5" height="4.2" rx="2.1" fill={a} opacity="0.66" />
          </svg>
        </div>
      );

    case 'zigzagVertical':
      return (
        <div className="relative flex h-[100px] w-[100px] items-center justify-center">
          <svg viewBox="0 0 100 100" className="h-[82px] w-[82px]">
            <path d="M24 14 L24 86 L42 14 L42 86 L60 14 L60 86 L78 14" fill="none" stroke={a} strokeWidth="4" opacity="0.68" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="24" cy="14" r="6" fill={soft} />
            <circle cx="42" cy="14" r="7" fill={a} />
            <circle cx="24" cy="86" r="5.5" fill={soft} opacity="0.9" />
            <circle cx="42" cy="86" r="5.5" fill={soft} opacity="0.9" />
            <circle cx="60" cy="14" r="5.5" fill={soft} opacity="0.9" />
            <circle cx="60" cy="86" r="5.5" fill={soft} opacity="0.9" />
            <circle cx="78" cy="14" r="5.5" fill={soft} opacity="0.9" />
          </svg>
        </div>
      );

    case 'zigzagHorizontal':
      return (
        <div className="relative flex h-[100px] w-[100px] items-center justify-center">
          <svg viewBox="0 0 100 100" className="h-[82px] w-[82px]">
            {[22, 36, 50, 64, 78].map((y, index) => (
              <path
                key={`row-${y}`}
                d={`M18 ${y} H84`}
                fill="none"
                stroke={soft}
                strokeWidth="2.8"
                opacity={0.72 - index * 0.08}
                strokeLinecap="round"
              />
            ))}

            <path d="M18 36 L84 22 L18 50 L84 36 L18 64 L84 50 L18 78" fill="none" stroke={soft} strokeWidth="2.2" opacity="0.56" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M18 22 H84" fill="none" stroke={a} strokeWidth="3.2" opacity="0.94" strokeLinecap="round" />
            <path d="M72 22 H84" fill="none" stroke={a} strokeWidth="3.2" opacity="0.94" strokeLinecap="round" />
            <path d="M72 22 L76.6 18.6 L76.6 25.4 Z" fill={a} opacity="0.94" />

            <circle cx="18" cy="22" r="6.1" fill={a} />
            {[36, 50, 64, 78].map((y, index) => (
              <circle key={`left-${y}`} cx="18" cy={y} r="4.8" fill={soft} opacity={0.46 - index * 0.05} />
            ))}
            {[22, 36, 50, 64, 78].map((y, index) => (
              <circle key={`right-${y}`} cx="84" cy={y} r="4.8" fill={soft} opacity={0.62 - index * 0.07} />
            ))}
          </svg>
        </div>
      );

    case 'zigzagVerticalLoop':
      return (
        <div className="relative flex h-[100px] w-[100px] items-center justify-center">
          <svg viewBox="0 0 100 100" className="h-[82px] w-[82px]">
            <path
              d="M12 52 L20 20 L34 44 L50 20 L66 44 L80 20 L88 52 L80 84 L66 60 L50 84 L34 60 L20 84 L12 52"
              fill="none"
              stroke={a}
              strokeWidth="3.8"
              opacity="0.68"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx="20" cy="20" r="6.4" fill={a} />
            <circle cx="50" cy="84" r="5.4" fill={soft} opacity="0.95" />
          </svg>
        </div>
      );

    case 'zigzagHorizontalLoop':
      return (
        <div className="relative flex h-[100px] w-[100px] items-center justify-center">
          <svg viewBox="0 0 100 100" className="h-[82px] w-[82px]">
            <path
              d="M26 28 L84 28 L26 40 L84 52 L26 64 L84 76 L18 88 L18 16 L84 16 L26 28"
              fill="none"
              stroke={a}
              strokeWidth="3.8"
              opacity="0.68"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx="26" cy="28" r="6.4" fill={a} />
            <circle cx="18" cy="88" r="5.4" fill={soft} opacity="0.95" />
          </svg>
        </div>
      );

    case 'hourglass':
      return (
        <div className="relative flex h-[100px] w-[100px] items-center justify-center">
          <svg viewBox="0 0 100 100" className="h-[82px] w-[82px]">
            <path d="M26 18 H74" fill="none" stroke={a} strokeWidth="4" opacity="0.68" strokeLinecap="round" />
            <path d="M26 82 H74" fill="none" stroke={a} strokeWidth="4" opacity="0.68" strokeLinecap="round" />
            <path d="M26 18 C 34 28, 41 37, 50 50 C 59 63, 66 72, 74 82" fill="none" stroke={a} strokeWidth="4" opacity="0.68" strokeLinecap="round" />
            <path d="M74 18 C 66 28, 59 37, 50 50 C 41 63, 34 72, 26 82" fill="none" stroke={a} strokeWidth="4" opacity="0.68" strokeLinecap="round" />
            <circle cx="50" cy="50" r="5.5" fill={a} />
          </svg>
        </div>
      );

    case 'guidedVertical':
      return (
        <div className="grid h-[88px] w-[88px] grid-cols-3 gap-1.5">
          {Array.from({ length: 12 }, (_, index) => (
            <span
              key={index}
              className="rounded-[10px]"
              style={{
                backgroundColor: index === 4 ? a : soft,
                opacity: index === 4 ? 1 : 0.55,
              }}
            />
          ))}
        </div>
      );

    case 'guidedHorizontal':
      return (
        <div className="grid h-[88px] w-[88px] grid-cols-4 gap-1.5">
          {Array.from({ length: 12 }, (_, index) => (
            <span
              key={index}
              className="rounded-[10px]"
              style={{
                backgroundColor: index === 6 ? a : soft,
                opacity: index === 6 ? 1 : 0.55,
              }}
            />
          ))}
        </div>
      );

    case 'columns':
      return (
        <div className="flex h-[90px] w-[100px] items-center justify-center gap-2">
          {[0, 1, 2, 3].map((column) => (
            <div key={column} className="flex flex-col gap-1.5">
              {[0, 1, 2, 3].map((row) => (
                <span
                  key={`${column}-${row}`}
                  className="block h-[12px] w-[14px] rounded-sm"
                  style={{
                    backgroundColor: row === 1 ? a : soft,
                    opacity: row === 1 ? 1 : 0.55,
                  }}
                />
              ))}
            </div>
          ))}
        </div>
      );

    default:
      return null;
  }
};

const getSectionVisual = (section: ModeSection, fallbackMode?: ModeConfig): SectionVisual => (
  SECTION_VISUALS[section.id] || {
    tone: fallbackMode?.tone || '#EEF7FA',
    accent: fallbackMode?.accent || '#168CA6',
    glow: fallbackMode?.accent || '#8ED5E5',
    illustration: 'reading',
  }
);

const CategoryIllustration: React.FC<{
  section: ModeSection;
  fallbackMode?: ModeConfig;
}> = ({ section, fallbackMode }) => {
  const visual = getSectionVisual(section, fallbackMode);
  const iconClassName = 'h-8 w-8';

  // Tenant'a özel bölüm görseli tanımlıysa (Settings.speedReadingSectionImages → katalog)
  // çizim yerine o görsel gösterilir. Tanımlı değilse aşağıdaki mevcut çizim korunur.
  if (section.imageUrl) {
    return (
      <div className="relative h-[172px] w-full overflow-hidden rounded-[1.35rem] bg-white">
        <img
          src={section.imageUrl}
          alt={section.title}
          loading="lazy"
          className="h-full w-full object-cover"
        />
      </div>
    );
  }

  const Icon = (() => {
    switch (visual.illustration) {
      case 'eye':
        return Eye;
      case 'words':
        return Brain;
      case 'attention':
        return Focus;
      case 'library':
        return Library;
      case 'homework':
        return ClipboardCheck;
      case 'reading':
      default:
        return BookOpen;
    }
  })();

  return (
    <div
      className="relative flex h-[172px] w-full items-center justify-center overflow-hidden rounded-[1.35rem]"
      style={{
        background: `linear-gradient(135deg, ${visual.tone} 0%, #ffffff 100%)`,
      }}
    >
      <span
        className="absolute -right-8 top-6 h-10 w-36 rotate-12 rounded-xl opacity-25"
        style={{ backgroundColor: visual.glow }}
      />
      <span
        className="absolute bottom-5 left-3 h-8 w-32 -rotate-12 rounded-xl opacity-20"
        style={{ backgroundColor: visual.accent }}
      />

      <div className="relative flex h-[118px] w-[118px] items-center justify-center">
        <div
          className="absolute bottom-0 h-[84px] w-[98px] rounded-[42px_42px_34px_34px] shadow-[0_18px_34px_rgba(23,78,103,0.12)]"
          style={{ backgroundColor: '#FFFFFF' }}
        />
        <div
          className="absolute bottom-[18px] left-[17px] h-[36px] w-[30px] rounded-full"
          style={{ backgroundColor: visual.accent, opacity: 0.25 }}
        />
        <div
          className="absolute bottom-[18px] right-[17px] h-[36px] w-[30px] rounded-full"
          style={{ backgroundColor: visual.accent, opacity: 0.25 }}
        />
        <div className="absolute top-[22px] flex gap-3">
          {[0, 1].map((eye) => (
            <span
              key={eye}
              className="flex h-[32px] w-[32px] items-center justify-center rounded-full border-[3px] bg-white"
              style={{ borderColor: visual.accent }}
            >
              <span className="h-[13px] w-[13px] rounded-full" style={{ backgroundColor: '#1D4758' }} />
            </span>
          ))}
        </div>
        <span
          className="absolute top-[56px] h-4 w-5 rounded-[0_0_10px_10px]"
          style={{ backgroundColor: visual.glow }}
        />
        <div
          className="absolute -right-1 bottom-[8px] flex h-[52px] w-[52px] items-center justify-center rounded-2xl border-[3px] bg-white shadow-[0_10px_24px_rgba(23,78,103,0.12)]"
          style={{ borderColor: `${visual.accent}33`, color: visual.accent }}
        >
          <Icon className={iconClassName} strokeWidth={2.3} />
        </div>
      </div>
    </div>
  );
};

export const ModeCard: React.FC<{ mode: ModeConfig; onClick?: () => void; isAvailable: boolean }> = ({ mode, onClick, isAvailable }) => (
  <button
    onClick={onClick}
    disabled={!isAvailable}
    className={`group flex flex-col rounded-2xl overflow-hidden border border-transparent transition-all duration-200 ${isAvailable
      ? 'cursor-pointer hover:border-gray-200 hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.98]'
      : 'cursor-default opacity-90'
      }`}
  >

    <div
      className="w-full flex items-center justify-center py-8"
      style={{ backgroundColor: mode.tone, minHeight: '160px' }}
    >
      <ModeIllustration mode={mode} />
    </div>

    <div className="flex flex-col items-start px-5 pt-4 pb-5 bg-white">
      <h3 className="text-base font-bold text-[#1e3a4c] tracking-tight leading-tight">
        {mode.name}
      </h3>
      {isAvailable ? (
        <span className="flex items-center gap-1 mt-2.5 text-[0.8rem] font-semibold text-[#1197b5] group-hover:text-[#0e7f9d] transition-colors">
          Hemen Başlayın
          <svg className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4.5 2.5L8 6L4.5 9.5" />
          </svg>
        </span>
      ) : (
        <span className="mt-2.5 text-[0.8rem] font-semibold text-[#91a4ae]">
          {mode.status === 'deprecated' ? 'Birleştirildi' : 'Yakında...'}
        </span>
      )}
    </div>
  </button>
);

export const SidebarPreparationCard: React.FC<{ paragraphs: string[]; fontFamily?: string }> = ({ paragraphs, fontFamily }) => {
  const [headline, ...details] = paragraphs;

  return (
    <div className="mt-6 rounded-xl border border-[#e4edf1] bg-[#f7fafc] p-4" style={fontFamily ? { fontFamily } : undefined}>
      <div className="text-xs font-semibold text-[#6c8591]">Hazırlık</div>
      {headline ? <div className="mt-2 text-sm font-bold text-[#27596a]">{headline}</div> : null}
      {details.map((paragraph, index) => (
        <p key={`${paragraph}-${index}`} className={`text-[13px] leading-6 text-[#5d7681] ${index === 0 ? 'mt-2' : 'mt-3'}`}>
          {paragraph}
        </p>
      ))}
    </div>
  );
};

export const ModesGrid: React.FC<{
  onSelectMode: (mode: ModeConfig) => void;
  modes?: ModeConfig[];
  sections?: ModeSection[];
}> = ({ onSelectMode, modes = [], sections = [] }) => {
  const [viewMode, setViewMode] = React.useState<'list' | 'cards'>(() => {
    if (typeof window === 'undefined') return 'cards';
    const saved = window.localStorage.getItem(MODES_GRID_VIEW_STORAGE_KEY);
    return saved === 'list' ? 'list' : 'cards';
  });
  const [listColumnMode, setListColumnMode] = React.useState<2 | 3>(() => {
    if (typeof window === 'undefined') return 3;
    return window.localStorage.getItem(MODES_GRID_LIST_COLUMNS_STORAGE_KEY) === '2' ? 2 : 3;
  });
  const [expandedListSections, setExpandedListSections] = React.useState<Record<string, boolean>>({});
  const [expandedCardSectionId, setExpandedCardSectionId] = React.useState<string | null>(null);
  const sortedSections = sections.slice().sort((left, right) => (left.order || 0) - (right.order || 0));

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(MODES_GRID_VIEW_STORAGE_KEY, viewMode);
  }, [viewMode]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(MODES_GRID_LIST_COLUMNS_STORAGE_KEY, String(listColumnMode));
  }, [listColumnMode]);

  const listSections = sortedSections
    .map((section) => ({
      section,
      sectionModes: modes.filter((mode) => mode.sectionId === section.id && mode.status !== 'hidden'),
    }))
    .filter(({ sectionModes }) => sectionModes.length > 0);
  const expandedCardSection = listSections.find(({ section }) => section.id === expandedCardSectionId) || null;

  if (viewMode === 'cards' && expandedCardSection) {
    const activeModeCount = expandedCardSection.sectionModes.filter((mode) => mode.status === 'active').length;

    return (
      <div className="h-full overflow-y-auto bg-[#f3f7fb] p-5 sm:p-8">
        <div className="mx-auto max-w-[88rem]">
          <div className="mb-7 border-b border-[#dbe7ec] pb-5">
            <div className="grid gap-4 lg:grid-cols-[12rem_minmax(0,1fr)_12rem] lg:items-center">
              <div className="flex items-center justify-start">
                <button
                  type="button"
                  onClick={() => setExpandedCardSectionId(null)}
                  className="inline-flex h-11 items-center gap-2 rounded-2xl border border-[#d4e2e8] bg-white px-4 text-sm font-extrabold text-[#174e67] shadow-[0_8px_22px_rgba(23,78,103,0.06)] transition-colors hover:border-[#b9d2dc] hover:bg-[#f8fcfd]"
                >
                  <ChevronLeft className="h-5 w-5" />
                  Geri
                </button>
              </div>

              <div className="text-center">
                <h2 className="text-[1.85rem] font-black tracking-tight text-[#174e67]">{expandedCardSection.section.title}</h2>
                {expandedCardSection.section.description ? (
                  <p className="mx-auto mt-1.5 max-w-3xl text-sm font-medium leading-6 text-[#6f8792]">{expandedCardSection.section.description}</p>
                ) : null}
              </div>

              <span className="justify-self-end rounded-2xl border border-[#cfe0e7] bg-white px-4 py-2 text-right text-sm font-extrabold text-[#174e67]">
                {activeModeCount} aktif çalışma
              </span>
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {expandedCardSection.sectionModes.map((mode) => (
              <ModeCard
                key={mode.id || mode.name}
                mode={mode}
                isAvailable={mode.status === 'active'}
                onClick={mode.status === 'active' ? () => onSelectMode(mode) : undefined}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-[#f3f7fb] p-5 sm:p-8">
      <div className="mx-auto max-w-[88rem]">
        <div className="mb-7 border-b border-[#dbe7ec] pb-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-[1.85rem] font-black tracking-tight text-[#174e67]">Hızlı Okuma Egzersizleri</h2>
              <p className="mt-1 text-sm font-medium text-[#6f8792]">Daha hızlı, daha dikkatli ve daha anlayarak okuyacaksın.</p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {viewMode === 'list' ? (
                <div className="inline-flex rounded-2xl border border-[#dbe7ec] bg-[#f7fbfc] p-1">
                  <button
                    type="button"
                    onClick={() => setListColumnMode(2)}
                    className={`flex h-9 w-10 items-center justify-center rounded-lg transition-colors ${listColumnMode === 2
                      ? 'bg-white text-[#174e67] shadow-[0_4px_14px_rgba(23,78,103,0.12)]'
                      : 'text-[#6f8792] hover:text-[#174e67]'
                      }`}
                    title="2 kolon"
                  >
                    <Grid2X2 className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setListColumnMode(3)}
                    className={`flex h-9 w-10 items-center justify-center rounded-lg transition-colors ${listColumnMode === 3
                      ? 'bg-white text-[#174e67] shadow-[0_4px_14px_rgba(23,78,103,0.12)]'
                      : 'text-[#6f8792] hover:text-[#174e67]'
                      }`}
                    title="3 kolon"
                  >
                    <Columns3 className="h-4 w-4" />
                  </button>
                </div>
              ) : null}

              <div className="inline-flex rounded-2xl border border-[#dbe7ec] bg-[#f7fbfc] p-1">
                <button
                  type="button"
                  onClick={() => setViewMode('list')}
                  className={`rounded-xl px-4 py-2 text-sm font-bold transition-colors ${viewMode === 'list'
                    ? 'bg-white text-[#174e67] shadow-[0_4px_18px_rgba(23,78,103,0.12)]'
                    : 'text-[#6f8792] hover:text-[#174e67]'
                    }`}
                >
                  Liste
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('cards')}
                  className={`rounded-xl px-4 py-2 text-sm font-bold transition-colors ${viewMode === 'cards'
                    ? 'bg-white text-[#174e67] shadow-[0_4px_18px_rgba(23,78,103,0.12)]'
                    : 'text-[#6f8792] hover:text-[#174e67]'
                    }`}
                >
                  Kategorili Kartlar
                </button>
              </div>
            </div>
          </div>
        </div>

        {viewMode === 'list' ? (
          <div className={`grid gap-6 ${listColumnMode === 2 ? 'xl:grid-cols-2' : 'xl:grid-cols-3'}`}>
            {listSections.map(({ section, sectionModes }, sectionIndex) => {
              const rowStartIndex = Math.floor(sectionIndex / listColumnMode) * listColumnMode;
              const rowSections = listSections.slice(rowStartIndex, rowStartIndex + listColumnMode);
              const rowMinModeCount = Math.min(...rowSections.map((rowSection) => rowSection.sectionModes.length));
              const isExpanded = Boolean(expandedListSections[section.id]);
              const hasHiddenModes = sectionModes.length > rowMinModeCount;
              const collapsedVisibleCount = Math.max(0, rowMinModeCount - 1);
              const visibleSectionModes = hasHiddenModes && !isExpanded
                ? sectionModes.slice(0, collapsedVisibleCount)
                : sectionModes;
              const hiddenModeCount = sectionModes.length - visibleSectionModes.length;

              return (
                <section
                  key={section.id}
                  className="flex flex-col rounded-[1.7rem] border border-[#dbe7ec] bg-white px-6 py-5 shadow-[0_8px_30px_rgba(23,78,103,0.05)]"
                >
                  <div className="mb-5 border-b border-[#ecf2f5] pb-4">
                    <h3 className="text-[1.3rem] font-extrabold tracking-tight text-[#1d4758]">{section.title}</h3>
                    {section.description ? (
                      <p className="mt-1.5 text-sm leading-6 text-[#67808c]">{section.description}</p>
                    ) : null}
                  </div>

                  <div className={`grid gap-2 ${listColumnMode === 2 ? 'sm:grid-cols-2' : ''}`}>
                    {visibleSectionModes.map((mode) => {
                      const isAvailable = mode.status === 'active';
                      return (
                        <button
                          key={mode.id || mode.name}
                          onClick={isAvailable ? () => onSelectMode(mode) : undefined}
                          disabled={!isAvailable}
                          className={`group flex items-center justify-between rounded-2xl border px-4 py-3 text-left transition-all ${isAvailable
                            ? 'border-[#e4edf1] bg-[#fbfdfe] hover:border-[#c6d9e2] hover:bg-[#f6fbfd]'
                            : 'border-[#edf2f5] bg-[#f8fafb] opacity-70'
                            }`}
                        >
                          <div className="min-w-0">
                            <div className="text-[15px] font-bold text-[#21485a]">{mode.name}</div>
                            <div className="mt-1 text-[12px] font-semibold text-[#2da3bf]">
                              {isAvailable ? 'Hemen Başlayın' : (mode.status === 'deprecated' ? 'Birleştirildi' : 'Yakında')}
                            </div>
                          </div>
                          <svg
                            className={`h-4 w-4 shrink-0 ${isAvailable ? 'text-[#1698b6] transition-transform group-hover:translate-x-0.5' : 'text-[#a4b4bb]'}`}
                            viewBox="0 0 12 12"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M4.5 2.5L8 6L4.5 9.5" />
                          </svg>
                        </button>
                      );
                    })}
                  </div>

                  {hasHiddenModes ? (
                    <button
                      type="button"
                      onClick={() => setExpandedListSections((prev) => ({ ...prev, [section.id]: !isExpanded }))}
                      className="mt-2 flex h-[70px] w-full items-center justify-center rounded-2xl border border-dashed border-[#cfe0e7] bg-[#f7fbfc] px-4 text-sm font-bold text-[#2f7188] transition-colors hover:border-[#acd0dc] hover:bg-[#eef7fa]"
                    >
                      {isExpanded ? 'Daha az göster' : `${hiddenModeCount} egzersiz daha var, göstermek için tıkla`}
                    </button>
                  ) : null}
                </section>
              );
            })}
          </div>
        ) : (
          <div className="space-y-8">
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {listSections.map(({ section, sectionModes }) => {
                const activeModes = sectionModes.filter((mode) => mode.status === 'active');
                const visualMode = activeModes[0] || sectionModes[0];
                const isExpanded = expandedCardSectionId === section.id;
                const sectionVisual = getSectionVisual(section, visualMode);

                return (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => setExpandedCardSectionId((current) => (current === section.id ? null : section.id))}
                    className={`group relative flex min-h-[282px] flex-col rounded-[1.55rem] border bg-white p-3 text-left transition-all duration-200 ${isExpanded
                      ? 'border-transparent shadow-[0_16px_38px_rgba(23,78,103,0.14)] ring-2 ring-offset-2'
                      : 'border-[#dbe7ec] shadow-[0_8px_26px_rgba(23,78,103,0.06)] hover:border-transparent hover:shadow-[0_14px_34px_rgba(23,78,103,0.1)] hover:-translate-y-0.5'
                      }`}
                    style={isExpanded ? ({ '--tw-ring-color': `${sectionVisual.accent}55` } as React.CSSProperties) : undefined}
                    aria-expanded={isExpanded}
                  >
                    <CategoryIllustration section={section} fallbackMode={visualMode} />
                    <div className="flex flex-1 flex-col px-2 pb-1 pt-4">
                      <div className="text-[17px] font-extrabold leading-tight text-[#1d4758]">
                        {section.title}
                      </div>
                      <div className="mt-2 text-sm font-semibold" style={{ color: sectionVisual.accent }}>
                        {activeModes.length || sectionModes.length} çalışma
                      </div>
                      {section.description ? (
                        <p className="mt-2 max-h-10 overflow-hidden text-[13px] leading-5 text-[#6f8792]">{section.description}</p>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
